import React, { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import { useParams, useNavigate } from 'react-router-dom';
import { MapContainer, TileLayer, Marker, Circle, Polyline, useMap } from 'react-leaflet';
import L from 'leaflet';
import { Play, Pause, MapPin, Navigation, ArrowLeft, ExternalLink, SkipForward, SkipBack, Rewind, FastForward, ChevronDown, ChevronUp, Car, Footprints, FlaskConical, Mail, Heart, Check, Sparkles, Compass, Activity, Clock, X } from 'lucide-react';
import { toast } from 'sonner';
import { useAuth } from '../App';
import ShareWalkCard from '../components/ShareWalkCard';
import TourCompletionExperience from '../components/TourCompletionExperience';
import {
  acceptedDeltaMeters,
  formatWalkedDistance,
  formatWalkedDuration,
  formatWalkingPace,
} from '../lib/walkingStats';
import 'leaflet/dist/leaflet.css';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: require('leaflet/dist/images/marker-icon-2x.png'),
  iconUrl: require('leaflet/dist/images/marker-icon.png'),
  shadowUrl: require('leaflet/dist/images/marker-shadow.png'),
});

const createCustomIcon = (isActive, isStart = false) => {
  if (isStart) {
    return L.divIcon({
      className: 'custom-marker start-marker',
      html: `<div style="position:relative;width:44px;height:44px;border-radius:50%;background:#0EA5E9;border:4px solid white;box-shadow:0 0 0 3px #0EA5E9,0 4px 12px rgba(0,0,0,0.3);display:flex;align-items:center;justify-content:center;color:white;font-weight:800;font-size:8px;letter-spacing:0.5px;line-height:1.05;text-align:center;">
        <span style="display:flex;flex-direction:column;align-items:center;">
          <span style="font-size:14px;">📍</span>
          <span>START</span>
        </span>
      </div>`,
      iconSize: [44, 44],
      iconAnchor: [22, 22],
    });
  }
  return L.divIcon({
    className: 'custom-marker',
    html: `<div class="${isActive ? 'active-marker' : ''} custom-marker"></div>`,
    iconSize: [24, 24],
    iconAnchor: [12, 12],
  });
};

const MapUpdater = ({ center }) => {
  const map = useMap();
  useEffect(() => {
    if (center) {
      map.setView(center, 16);
    }
  }, [center, map]);
  return null;
};

const TourPlayerPage = () => {
  const { purchaseId } = useParams();
  const navigate = useNavigate();
  const { token, user } = useAuth();
  const isAdmin = user?.account_type === 'admin';
  const searchParams = new URLSearchParams(window.location.search);
  const tourMode = searchParams.get('mode'); // 'test' or 'regular'
  const [analyticsSessionId] = useState(() => `sess-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`);
  const [stops, setStops] = useState([]);
  const [allStops, setAllStops] = useState([]); // Store all stops before filtering
  const [currentStopIndex, setCurrentStopIndex] = useState(0);
  const [userLocation, setUserLocation] = useState(null);
  const [playing, setPlaying] = useState(false);
  const [audio, setAudio] = useState(null);
  const [purchase, setPurchase] = useState(null);
  const [loading, setLoading] = useState(true);
  const [audioCompleted, setAudioCompleted] = useState(false);
  const [showStopsList, setShowStopsList] = useState(false);
  const [tourType, setTourType] = useState('walking');
  const [introPlayed, setIntroPlayed] = useState(false);
  const [showIntroPrompt, setShowIntroPrompt] = useState(true);
  const [showNavigationChoice, setShowNavigationChoice] = useState(false);
  const [preferredNavApp, setPreferredNavApp] = useState('google'); // 'google' or 'waze'
  const [showVersionChoice, setShowVersionChoice] = useState(false); // NEW: Show version choice screen
  const [selectedVersion, setSelectedVersion] = useState(null); // NEW: 'full' or 'express'
  const [hasExpressOption, setHasExpressOption] = useState(false); // NEW: Does this tour have express?
  const [showFullTourMap, setShowFullTourMap] = useState(true); // Show map of all stops — opens by default to reduce taps
  
  // Tour completion & newsletter states
  const [tourCompleted, setTourCompleted] = useState(false);
  const [newsletterEmail, setNewsletterEmail] = useState('');
  const [newsletterName, setNewsletterName] = useState('');
  const [newsletterSubmitting, setNewsletterSubmitting] = useState(false);
  const [newsletterDone, setNewsletterDone] = useState(false);
  // Share My Walk
  const [showShareCard, setShowShareCard] = useState(false);
  const [stridesEarnedThisSession, setStridesEarnedThisSession] = useState(0);
  const [newlyUnlockedAchievements, setNewlyUnlockedAchievements] = useState([]);

  // Resume-from-where-you-left-off
  //   Per-purchase saved position. Persisted every time the current stop
  //   changes AND every 5s of audio playback (throttled). Cleared when the
  //   tour completes OR when the user hits "Start over".
  //
  //   Storage lives in TWO layers:
  //     • localStorage (per-device, keyed by purchase_id) — instant, offline-safe.
  //     • backend `/api/tour-progress/{tour_id}` (per-user, cross-device) —
  //       so a traveler who switches phones mid-tour picks up where they
  //       left off. Sync happens on every stop-change + every ~30s of
  //       playback (throttled separately from local).
  //   On load, we compare the two by `updated_at` and use whichever is fresher.
  const resumeStorageKey = purchaseId ? `audiostride_resume:${purchaseId}` : null;
  const savedResumeTimeRef = useRef(null); // audio seconds to seek to on first play after resume
  const lastResumeSaveRef = useRef(0); // throttling timestamp for LOCAL timeupdate saves
  const lastBackendSaveRef = useRef(0); // throttling timestamp for BACKEND syncs
  const resumeAppliedRef = useRef(false); // set once we've handled a resume for this mount


  // "Replay directions" pulse. Set true when a non-final stop's audio
  // finishes so the button can softly pulse for ~6 seconds — the
  // "okay, what now?" ambient nudge. Cleared on tap or timeout.
  const [directionsPulse, setDirectionsPulse] = useState(false);

  // Wake Lock — keeps the iPhone/Android screen on for the entire tour
  // session (not just while audio is playing). Critical: we MUST hold the
  // lock through the gaps between stops, because that's when iOS throttles
  // setInterval AND watchPosition. iOS 16.4+ and modern Android Chrome
  // support this. Silent no-op on older browsers. Released on tour end,
  // unmount, or auto-released by iOS when the tab is hidden.
  const wakeLockRef = useRef(null);
  const wakeLockToastShownRef = useRef(false);
  const wakeLockFailedRef = useRef(false);
  // Detect iOS Safari for Low Power Mode guidance. Mac Safari uses the same
  // UA pattern so we also require touch support, which excludes desktop.
  const isIOSSafari = (() => {
    if (typeof navigator === 'undefined') return false;
    const ua = navigator.userAgent || '';
    const isIOS = /iPad|iPhone|iPod/.test(ua) ||
      (/Macintosh/.test(ua) && (navigator.maxTouchPoints || 0) > 1);
    const isSafari = /^((?!chrome|crios|fxios|edgios|opios).)*safari/i.test(ua);
    return isIOS && isSafari;
  })();
  // Show one-time modal guiding the user to disable Low Power Mode. Set
  // when Wake Lock request fails on iOS Safari (a strong but indirect
  // signal that LPM is on).
  const [showLowPowerWarning, setShowLowPowerWarning] = useState(false);
  const requestWakeLock = async () => {
    if (!('wakeLock' in navigator)) {
      console.log('[Wake Lock] not supported on this browser');
      return;
    }
    if (wakeLockRef.current) return; // already held
    try {
      wakeLockRef.current = await navigator.wakeLock.request('screen');
      console.log('[Wake Lock] acquired');
      wakeLockFailedRef.current = false;
      // One-time confirmation toast per session so the user knows it engaged.
      if (!wakeLockToastShownRef.current) {
        wakeLockToastShownRef.current = true;
        toast.success('Screen will stay awake for your tour', { duration: 4000 });
      }
      wakeLockRef.current.addEventListener?.('release', () => {
        console.log('[Wake Lock] released');
        wakeLockRef.current = null;
      });
    } catch (err) {
      console.log('[Wake Lock] request failed', err);
      // iOS Safari throws when Low Power Mode is on (and a handful of other
      // edge cases). Show the LPM guidance modal — once per session — so
      // users know they may be in LPM. We deliberately say "may" because we
      // can't be 100% certain.
      if (isIOSSafari && !wakeLockFailedRef.current) {
        wakeLockFailedRef.current = true;
        setShowLowPowerWarning(true);
      }
    }
  };
  const releaseWakeLock = () => {
    const lock = wakeLockRef.current;
    if (!lock) return;
    try { lock.release?.(); } catch (_) { /* noop */ }
    wakeLockRef.current = null;
  };
  
  // Test mode states (admin only)
  const [testMode, setTestMode] = useState(false);
  const [testMaps, setTestMaps] = useState([]);
  const [selectedTestMap, setSelectedTestMap] = useState(null);
  const [showTestModePanel, setShowTestModePanel] = useState(false);
  
  const audioTriggeredRef = useRef(false);
  const currentStopIndexRef = useRef(0);
  const stopsRef = useRef([]);
  const userLocationRef = useRef(null);
  const proximityIntervalRef = useRef(null);
  const playStopAudioRef = useRef(null);
  const persistentAudioRef = useRef(null); // Single reusable Audio element
  // Hidden waypoints — GPS-triggered secret audio that isn't part of the
  // tour sequence. Stored in a ref (not state) so the geofence loop can
  // read them without re-registering the checker.
  const hiddenWaypointsRef = useRef([]); // Array of stop docs where is_hidden=true
  const hiddenFiredRef = useRef(new Set()); // Set of stop_ids already fired this session
  const waypointPlayingRef = useRef(false); // True while a waypoint's audio is playing
  // Contextual eligibility state for hidden waypoints.
  //   armedAt: Map<waypoint_stop_id, epoch_ms> — set when the parent stop's
  //     audio finishes. If the waypoint has no trigger_after_stop_number,
  //     it's armed from tour start (armedAt = 0).
  //   expired: Set<waypoint_stop_id> — waypoints that missed their window
  //     (parent's expires_when stop was reached, wrong-stop arrival, or
  //     time-based timeout hit). Never fires this session.
  const waypointArmedAtRef = useRef(new Map());
  const expiredWaypointsRef = useRef(new Set());
  // Track which visible stops have finished playing. Populated by the
  // stop_completed audio.onended handler. Used to arm/expire waypoints.
  const completedVisibleStopsRef = useRef(new Set());
  // Snapshot of the primary audio state we saved when a "warn" waypoint
  // interrupted playback — used to restore + resume the tour audio after
  // the waypoint finishes.
  const primaryAudioSnapshotRef = useRef(null);
  // Stable proximity check — installed by an effect, called from BOTH the
  // foreground setInterval AND the watchPosition callback. The watchPosition
  // path is the one that survives iOS screen-sleep, because iOS throttles
  // setInterval but keeps GPS callbacks firing while the audio session is
  // alive (the Silent Bridge keeps it alive between stops).
  const checkProximityRef = useRef(null);

  // ─── Walking stats ────────────────────────────────────────────────────
  // Live GPS-accumulated distance (metres) and wall-clock time since the
  // first fetched position. Both survive re-renders and are read by the
  // Stats modal + the Completion screen.
  //
  //   walkedMetersRef       — running sum of accepted geolocation deltas
  //   lastAcceptedFixRef    — last { lat, lng, accuracy_m } we counted
  //   walkStartTsRef        — epoch ms of the first accepted fix (or tour
  //                           start, whichever fires first). Drives elapsed
  //                           time even for users whose GPS is slow to lock.
  //   walkStatsTickRef      — bump to re-render Stats modal every second
  const walkedMetersRef = useRef(0);
  const lastAcceptedFixRef = useRef(null);
  const walkStartTsRef = useRef(null);
  const [walkStatsTick, setWalkStatsTick] = useState(0);
  const [showStatsModal, setShowStatsModal] = useState(false);
  
  // Audio timer states
  const [audioDuration, setAudioDuration] = useState(0);
  const [audioCurrentTime, setAudioCurrentTime] = useState(0);
  const audioIntervalRef = useRef(null);

  // Resume-banner state — shown when iOS suspends/interrupts the audio
  // session (incoming call, long screen-lock, switching to Spotify, etc.).
  // A single tap on the banner restarts whatever was running.
  const [needsResume, setNeedsResume] = useState(false);
  // Tracks what state we were in when the session died so we can restore it
  const playbackStateRef = useRef({ mode: 'idle' });
  // mode: 'idle' | 'playing-stop' | 'silent-bridge'
  // Set to true right before WE intentionally pause, so the audio element's
  // `pause` event handler doesn't misinterpret it as an interruption.
  const intentionalPauseRef = useRef(false);

  // Format time in MM:SS
  const formatTime = (seconds) => {
    if (isNaN(seconds) || seconds === Infinity) return '0:00';
    const mins = Math.floor(seconds / 60);
    const secs = Math.floor(seconds % 60);
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  // Calculate time remaining
  const getTimeRemaining = () => {
    const remaining = audioDuration - audioCurrentTime;
    return remaining > 0 ? remaining : 0;
  };

  // ─── Walking stats (live) ─────────────────────────────────────────────
  // Snapshot of the current session's stats. Reads from refs so we don't
  // trigger renders on every GPS fix; the Stats modal + Pause banner get
  // a fresh value each render via the 1-second tick effect below.
  const getWalkStats = () => {
    const meters = walkedMetersRef.current || 0;
    const started = walkStartTsRef.current;
    const seconds = started ? Math.max(0, Math.round((Date.now() - started) / 1000)) : 0;
    return { meters, seconds };
  };

  // Tick every second while the Stats modal is open so the numbers stay
  // fresh even when audio is paused. We deliberately DON'T tick when the
  // modal is closed — no wasted renders during normal playback.
  useEffect(() => {
    if (!showStatsModal) return undefined;
    const id = setInterval(() => setWalkStatsTick((t) => t + 1), 1000);
    return () => clearInterval(id);
  }, [showStatsModal]);

  useEffect(() => {
    if (!token || !user) return; // Wait for auth to load
    
    fetchPurchaseAndStops();
    startGPSTracking();
    
    // Fetch test maps if admin
    if (user?.account_type === 'admin') {
      fetchTestMaps();
    }

    return () => {
      if (audio) {
        audio.pause();
      }
      if (audioIntervalRef.current) {
        clearInterval(audioIntervalRef.current);
      }
    };
  }, [purchaseId, token, user]);
  
  // Fetch test maps for admin
  const fetchTestMaps = async () => {
    try {
      const response = await axios.get(`${API}/admin/test-maps`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setTestMaps(response.data);
    } catch (error) {
      console.error('Failed to fetch test maps:', error);
    }
  };
  
  // Enable/disable test mode
  const handleTestModeToggle = async () => {
    if (testMode) {
      // Disable test mode - reload original stops
      setTestMode(false);
      setSelectedTestMap(null);
      toast.success('Test mode disabled');
      // Refetch stops without test map
      await refetchStopsWithTestMap(null);
    } else {
      setShowTestModePanel(true);
    }
  };
  
  // Apply test map and refetch stops
  const applyTestMap = async (testMap) => {
    setSelectedTestMap(testMap);
    setTestMode(true);
    setShowTestModePanel(false);
    toast.success(`Test mode enabled: ${testMap.name}`);
    
    const tourId = purchase?.tour_id;
    if (!tourId) return;
    
    try {
      const url = `${API}/tours/${tourId}/stops?test_map_id=${testMap.map_id}`;
      const stopsResponse = await axios.get(url, {
        headers: { Authorization: `Bearer ${token}` }
      });
      const rawStops = stopsResponse.data.sort((a, b) => a.stop_number - b.stop_number);
      // Re-apply the hidden/visible split so hidden waypoints don't leak
      // into the public list during a test-map preview.
      hiddenWaypointsRef.current = rawStops.filter((s) => s && s.is_hidden === true && s.latitude && s.longitude);
      let sortedStops = rawStops.filter((s) => !s || s.is_hidden !== true);
      
      if (selectedVersion === 'express') {
        sortedStops = filterForExpress(sortedStops);
      }
      
      // Update stops but keep current position
      setStops(sortedStops);
      setAllStops(sortedStops);
      stopsRef.current = sortedStops;
      
      console.log('[TestMode] Stops updated. Current idx:', currentStopIndexRef.current);
      if (sortedStops[currentStopIndexRef.current]) {
        const s = sortedStops[currentStopIndexRef.current];
        console.log(`[TestMode] Current stop "${s.title}": lat=${s.latitude}, lng=${s.longitude}`);
      }
      
      // Immediately check if user is already at the current stop with new coords
      const idx = currentStopIndexRef.current;
      const nextStop = sortedStops[idx];
      const loc = userLocationRef.current;
      if (loc && nextStop && !nextStop.is_intro && nextStop.latitude && nextStop.longitude) {
        const lat = parseFloat(nextStop.latitude);
        const lng = parseFloat(nextStop.longitude);
        if (!isNaN(lat) && !isNaN(lng)) {
          const R = 6371000;
          const dLat = ((lat - loc.lat) * Math.PI) / 180;
          const dLon = ((lng - loc.lng) * Math.PI) / 180;
          const a = Math.sin(dLat / 2) * Math.sin(dLat / 2) +
            Math.cos((loc.lat * Math.PI) / 180) * Math.cos((lat * Math.PI) / 180) *
            Math.sin(dLon / 2) * Math.sin(dLon / 2);
          const distance = R * 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
          const radius = Math.max(nextStop.radius_meters || 50, 30);
          console.log(`[TestMode] Distance to "${nextStop.title}": ${Math.round(distance)}m / ${radius}m`);
          
          if (distance <= radius && !audioTriggeredRef.current) {
            audioTriggeredRef.current = true;
            if (playStopAudioRef.current) {
              playStopAudioRef.current(nextStop);
              toast.success(`Reached: ${nextStop.title}`);
            }
          }
        }
      }
    } catch (error) {
      toast.error('Failed to apply test map');
    }
  };
  
  // Refetch stops with or without test map
  const refetchStopsWithTestMap = async (testMapId) => {
    try {
      const tourId = purchase?.tour_id;
      if (!tourId) return;
      
      const url = testMapId 
        ? `${API}/tours/${tourId}/stops?test_map_id=${testMapId}`
        : `${API}/tours/${tourId}/stops`;
      
      const stopsResponse = await axios.get(url, {
        headers: { Authorization: `Bearer ${token}` }
      });
      const rawStops = stopsResponse.data.sort((a, b) => a.stop_number - b.stop_number);
      // Re-apply the hidden/visible split — same as the initial fetch and
      // the test-map preview — so hidden waypoints stay secret and the
      // geofence loop keeps tracking them after a test-map toggle.
      hiddenWaypointsRef.current = rawStops.filter((s) => s && s.is_hidden === true && s.latitude && s.longitude);
      let sortedStops = rawStops.filter((s) => !s || s.is_hidden !== true);
      
      // Apply version filter if needed
      if (selectedVersion === 'express') {
        sortedStops = filterForExpress(sortedStops);
      }
      
      setStops(sortedStops);
      setAllStops(sortedStops);
      // Don't reset currentStopIndex — keep the tour position
      
      // Update refs immediately
      stopsRef.current = sortedStops;
      
      console.log(`[TestMode] Stops updated with${testMapId ? ' test map' : 'out test map'}. Stop 1 coords:`, 
        sortedStops[1] ? `lat=${sortedStops[1].latitude}, lng=${sortedStops[1].longitude}` : 'no stop 1');
    } catch (error) {
      toast.error('Failed to reload stops');
    }
  };

  useEffect(() => {
    currentStopIndexRef.current = currentStopIndex;
    // Persist tour progress so HomePage can offer a "Resume" banner
    try {
      if (purchase?.tour && stops.length > 0) {
        localStorage.setItem('audiostride_last_tour', JSON.stringify({
          purchase_id: purchaseId,
          tour_id: purchase.tour.tour_id,
          tour_title: purchase.tour.title,
          city: purchase.tour.city,
          current_stop: currentStopIndex,
          total_stops: stops.length,
          last_seen: Date.now(),
        }));
      }
    } catch (_) { /* ignore quota errors */ }
    // Per-purchase resume state so the SAME player instance jumps back to
    // the exact stop next time the user opens it. Keyed by purchase_id so
    // multiple in-flight tours don't collide. `version` prevents Full/Express
    // cross-contamination.
    //
    // IMPORTANT: don't clobber saved progress on initial mount. If the
    // user was mid-tour last session, `currentStopIndex` starts at 0 on
    // this render (before `readResume` gets a chance to bump it), so
    // writing here would erase their real position. We skip the write
    // until either (a) a resume was actually applied, or (b) the user
    // has advanced past stop 0 — both signals that we're now writing
    // ACTUAL progress, not the initial render state. The timeupdate
    // handler covers the "user is truly at stop 0, playing" case.
    try {
      const shouldWrite =
        resumeStorageKey &&
        stops.length > 0 &&
        selectedVersion &&
        (resumeAppliedRef.current || currentStopIndex > 0);
      if (shouldWrite) {
        const stop = stops[currentStopIndex];
        const raw = localStorage.getItem(resumeStorageKey);
        const prev = raw ? JSON.parse(raw) : null;
        const patch = {
          stop_index: currentStopIndex,
          stop_id: stop?.stop_id || null,
          version: selectedVersion,
          completed: false,
          // preserve prev current_time — new stop's real time comes from
          // the audio timeupdate handler once it starts playing.
          current_time: currentStopIndex === (prev?.stop_index ?? -1) ? (prev?.current_time || 0) : 0,
        };
        localStorage.setItem(resumeStorageKey, JSON.stringify({
          ...(prev || {}),
          ...patch,
          updated_at: Date.now(),
        }));
        // Also sync to the backend so a device swap sees the new stop
        // immediately (bypasses the 30s timeupdate throttle for the
        // higher-signal "user just advanced" event).
        pushBackendProgress(patch);
        lastBackendSaveRef.current = Date.now();
      }
    } catch (_) { /* ignore */ }
  }, [currentStopIndex, purchase, stops, purchaseId, resumeStorageKey, selectedVersion]);

  useEffect(() => {
    stopsRef.current = stops;
  }, [stops]);

  // Keep userLocation ref in sync
  useEffect(() => {
    userLocationRef.current = userLocation;
  }, [userLocation]);

  // Install the stable proximity-check function. Runs on every render but
  // only reassigns a ref — cheap. The function reads from refs only, so
  // both call sites (foreground interval + background watchPosition) see
  // the same up-to-date state.
  checkProximityRef.current = (callerTag) => {
    const loc = userLocationRef.current;
    const currentStops = stopsRef.current;
    const idx = currentStopIndexRef.current;

    // Always check hidden waypoints — they fire independently of the
    // primary tour sequence and don't require a "next stop" to exist.
    try { checkHiddenWaypoints(); } catch (e) { console.log('[Hidden WP] check failed:', e); }

    if (!loc || currentStops.length === 0) return;

    const nextStop = currentStops[idx];
    if (!nextStop) return;
    if (nextStop.is_intro) return;

    const lat = parseFloat(nextStop.latitude);
    const lng = parseFloat(nextStop.longitude);
    if (isNaN(lat) || isNaN(lng)) return;

    const R = 6371000;
    const dLat = ((lat - loc.lat) * Math.PI) / 180;
    const dLon = ((lng - loc.lng) * Math.PI) / 180;
    const a = Math.sin(dLat / 2) * Math.sin(dLat / 2) +
      Math.cos((loc.lat * Math.PI) / 180) * Math.cos((lat * Math.PI) / 180) *
      Math.sin(dLon / 2) * Math.sin(dLon / 2);
    const distance = R * 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
    const radius = nextStop.radius_meters || 20;

    console.log(`[GPS:${callerTag || 'tick'}] Stop ${idx} "${nextStop.title}": ${Math.round(distance)}m / ${radius}m, triggered=${audioTriggeredRef.current}`);

    if (distance <= radius && !audioTriggeredRef.current) {
      audioTriggeredRef.current = true;
      if (playStopAudioRef.current) {
        playStopAudioRef.current(nextStop);
      }
      toast.success(`Reached: ${nextStop.title}`);
    }
  };

  // Continuous proximity check using interval — primary trigger when the
  // tab is in the foreground. iOS throttles or pauses this when the screen
  // sleeps, which is why we ALSO call checkProximityRef from inside the
  // watchPosition callback (see onPosition further down).
  useEffect(() => {
    proximityIntervalRef.current = setInterval(() => {
      checkProximityRef.current?.('interval');
    }, 2000); // Check every 2 seconds

    return () => {
      if (proximityIntervalRef.current) {
        clearInterval(proximityIntervalRef.current);
      }
    };
  }, []); // Runs once, reads from refs

  // When the tab becomes visible again (user wakes their phone), do an
  // immediate catch-up proximity check. If iOS throttled background GPS
  // and the user walked into a geofence while the screen was off, this
  // catches it the moment the screen comes back on.
  useEffect(() => {
    const onVisibility = () => {
      if (document.visibilityState === 'visible') {
        checkProximityRef.current?.('visibility');
      }
    };
    document.addEventListener('visibilitychange', onVisibility);
    return () => document.removeEventListener('visibilitychange', onVisibility);
  }, []);

  // Media Session API — tells iOS / Android "this tab is a real media
  // player". iOS gives such tabs better background treatment (less
  // aggressive throttling) and shows tour metadata on the lock screen.
  // Purely additive: doesn't touch the audio element or playback flow.
  useEffect(() => {
    if (!('mediaSession' in navigator)) return;
    const stop = stops[currentStopIndex];
    const tourTitle = purchase?.tour?.title || 'Audiostride';
    try {
      // eslint-disable-next-line no-undef
      navigator.mediaSession.metadata = new window.MediaMetadata({
        title: stop?.title || tourTitle,
        artist: tourTitle,
        album: 'Audiostride · GPS Audio Tour',
        artwork: purchase?.tour?.cover_image_url
          ? [{ src: purchase.tour.cover_image_url, sizes: '512x512', type: 'image/jpeg' }]
          : [],
      });
    } catch (_) { /* MediaMetadata not supported — non-fatal */ }
  }, [currentStopIndex, stops, purchase]);

  // Wake Lock — request when the tour is active and release when it ends
  // or the user exits. Critical: we hold the lock through the GAPS between
  // stops (when audio is paused but the user is still walking to the next
  // location), because that's when iOS throttles setInterval AND
  // watchPosition. If we only held it during playback, geofence triggers
  // would fire late or not at all. Released on tour completion, unmount,
  // or when the page is hidden (iOS auto-releases anyway on hidden).
  useEffect(() => {
    // The tour is "active" as long as we have stops and haven't shown the
    // completion screen.
    const tourActive = stops.length > 0 && !tourCompleted;
    if (tourActive) {
      requestWakeLock();
    } else {
      releaseWakeLock();
    }
    return () => releaseWakeLock();
  }, [stops.length, tourCompleted]);

  useEffect(() => {
    const onVis = () => {
      // iOS auto-releases Wake Lock when the tab becomes hidden. When the
      // tab comes back, re-acquire if the tour is still active.
      if (document.visibilityState === 'visible' && stops.length > 0 && !tourCompleted) {
        requestWakeLock();
      }
    };
    document.addEventListener('visibilitychange', onVis);
    return () => document.removeEventListener('visibilitychange', onVis);
  }, [stops.length, tourCompleted]);

  const fetchPurchaseAndStops = async () => {
    // Declared OUTSIDE the try so the finally block can inspect them.
    // Fixes the "0 tour starts in analytics" bug: previously the finally
    // used React state `purchase`, which hasn't flushed yet on first mount,
    // so the tour_started event never fired.
    let currentPurchase;
    let resolvedTourId;
    try {
      // Check if this is an admin test (admin-test-{tour_id})
      const isAdminTest = purchaseId.startsWith('admin-test-');
      const currentIsAdmin = user?.account_type === 'admin';
      let tourId;
      
      if (isAdminTest && currentIsAdmin) {
        // Admin testing a tour directly
        tourId = purchaseId.replace('admin-test-', '');
        
        // Create a fake purchase object for the tour player
        const tourResponse = await axios.get(`${API}/tours/${tourId}`);
        currentPurchase = {
          purchase_id: purchaseId,
          tour_id: tourId,
          tour: tourResponse.data,
          active: true,
          tour_version: 'full_access',
          expires_at: new Date(Date.now() + 86400000).toISOString(), // 24 hours from now
          is_admin_test: true
        };
      } else {
        // Normal user - fetch their purchase
        const purchasesResponse = await axios.get(`${API}/purchases/my-tours`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        currentPurchase = purchasesResponse.data.find((p) => p.purchase_id === purchaseId);

        if (!currentPurchase || !currentPurchase.active) {
          toast.error('Tour not accessible');
          navigate('/my-tours');
          return;
        }
        tourId = currentPurchase.tour_id;
      }

      setPurchase(currentPurchase);
      resolvedTourId = tourId;
      
      // Get the purchased tour version
      const tourVersion = currentPurchase.tour_version || 'comprehensive';

      // Fetch tour details to get tour_type and express_enabled
      let tourHasExpress = false;
      try {
        const tourResponse = await axios.get(`${API}/tours/${tourId}`);
        setTourType(tourResponse.data.tour_type || 'walking');
        tourHasExpress = tourResponse.data.express_enabled || false;
      } catch (e) {
        setTourType('walking');
      }

      // If mode=test, fetch test maps and apply first one to initial stop load
      let testMapId = null;
      if (tourMode === 'test' && isAdminTest && currentIsAdmin) {
        try {
          const mapsRes = await axios.get(`${API}/admin/test-maps`, {
            headers: { Authorization: `Bearer ${token}` }
          });
          if (mapsRes.data && mapsRes.data.length > 0) {
            const firstMap = mapsRes.data[0];
            testMapId = firstMap.map_id;
            setTestMaps(mapsRes.data);
            setSelectedTestMap(firstMap);
            setTestMode(true);
            console.log(`[TestMode] Auto-applied test map: ${firstMap.name}`);
          }
        } catch (e) {
          console.error('Failed to load test maps:', e);
        }
      }

      const stopsUrl = testMapId 
        ? `${API}/tours/${tourId}/stops?test_map_id=${testMapId}`
        : `${API}/tours/${tourId}/stops`;
      const stopsResponse = await axios.get(stopsUrl, {
        headers: { Authorization: `Bearer ${token}` }
      });
      const rawStops = stopsResponse.data.sort((a, b) => a.stop_number - b.stop_number);

      // Split visible tour stops from hidden waypoints. Hidden waypoints
      // are geofence-triggered secrets that never appear in the public
      // list, never count toward progress, and fire once per session.
      hiddenWaypointsRef.current = rawStops.filter((s) => s && s.is_hidden === true && s.latitude && s.longitude);
      let sortedStops = rawStops.filter((s) => !s || s.is_hidden !== true);

      // Rehydrate the "already fired" set from localStorage so backtracks
      // and page refreshes don't re-trigger the same waypoint.
      try {
        const key = `hidden_fired:${tourId}`;
        const raw = localStorage.getItem(key);
        const arr = raw ? JSON.parse(raw) : [];
        hiddenFiredRef.current = new Set(Array.isArray(arr) ? arr : []);
      } catch (_) {
        hiddenFiredRef.current = new Set();
      }
      
      // Store all stops for later filtering
      setAllStops(sortedStops);
      
      // Check if any stops are marked for express
      const expressStopsExist = sortedStops.some(stop => stop.include_in_express === true);
      setHasExpressOption(tourHasExpress && expressStopsExist);
      
      // If tour_version is 'full_access' and has express option, show version choice
      if (tourVersion === 'full_access' && tourHasExpress && expressStopsExist) {
        setShowVersionChoice(true);
        setStops(sortedStops); // Set full stops initially
        setLoading(false);
        return; // Don't continue - wait for user to choose version
      }
      
      // Handle legacy purchases with specific versions
      if (tourVersion === 'express') {
        sortedStops = filterForExpress(sortedStops);
        setSelectedVersion('express');
      } else {
        setSelectedVersion('full');
      }
      
      setStops(sortedStops);
      
      // Resume from where the user left off (if any). Fetch backend
      // progress in parallel so we can pick whichever is more recent
      // (local vs cross-device). Then let readResume decide.
      // NOTE: use `currentPurchase` here, not `purchase` — React state
      // set on line 626 hasn't flushed yet, so `purchase` is still `null`
      // and would throw "null is not an object (evaluating '.tour')".
      const progressTourId = currentPurchase.tour?.tour_id || currentPurchase.tour_id || tourId;
      const backendProgress = await fetchBackendProgress(progressTourId);
      const resume = readResume(tourVersion === 'express' ? 'express' : 'full', sortedStops, backendProgress);
      if (resume) {
        resumeAppliedRef.current = true;
        savedResumeTimeRef.current = resume.currentTime;
        setCurrentStopIndex(resume.stopIndex);
        // Fire the resume stop after a short delay so state has settled.
        setTimeout(() => {
          if (playStopAudioRef.current && resume.stop) {
            playStopAudioRef.current(resume.stop);
          }
        }, 1500);
        // Discreet, actionable notice — user can start over if they want.
        toast.success(
          `Resumed at Stop ${resume.stopIndex + 1}: ${resume.stop?.title || ''}`,
          { duration: 8000, action: { label: 'Start over', onClick: () => restartTour() } },
        );
      } else if (sortedStops.length > 0 && (sortedStops[0].is_intro || sortedStops[0].stop_number === 0)) {
        // No resume state — go straight to the main player and auto-play the intro.
        setTimeout(() => {
          if (playStopAudioRef.current && sortedStops[0]) {
            playStopAudioRef.current(sortedStops[0]);
            toast.success('Tour started! Listen to the introduction.');
          }
        }, 1500);
      }
    } catch (error) {
      // Diagnostic path — the previous silent redirect made bugs invisible.
      // Now we log to console AND show admins a persistent toast with the
      // real error, so they know exactly why a tour won't open.
      const status = error?.response?.status;
      const detail = error?.response?.data?.detail;
      const url = error?.config?.url;
      const method = error?.config?.method?.toUpperCase();
      console.error('[TourPlayer] Fatal load error', { status, detail, url, method, error });
      const isAdminUser = user?.account_type === 'admin';
      if (isAdminUser) {
        toast.error(
          `Player load failed (${status || 'network'}): ${detail || error?.message || 'unknown'} — ${method} ${url || ''}`,
          { duration: 15000 },
        );
      } else {
        toast.error('Failed to load tour data');
      }
      navigate(isAdminUser ? '/admin' : '/my-tours');
      return;
    } finally {
      setLoading(false);
      // Track tour_started using the LOCAL currentPurchase (not React state,
      // which is still null here on first mount). This is the fix for
      // "0 tour starts in analytics dashboard".
      const startedTourId = currentPurchase?.tour_id || resolvedTourId;
      if (startedTourId && user?.email) {
        trackEvent('tour_started', {
          entry_point: tourMode === 'test' ? 'admin_test' : 'direct',
          tour_id: startedTourId,
        });
        // Anchor the walking-time clock the moment the tour officially
        // starts so users see elapsed time even before their first GPS fix.
        if (walkStartTsRef.current == null) {
          walkStartTsRef.current = Date.now();
        }
      }
    }
  };
  
  // Helper function to filter stops for express version
  const filterForExpress = (stopsToFilter) => {
    console.log('[TourPlayer] Loading EXPRESS tour - filtering stops');
    let expressStops = stopsToFilter.filter(stop => stop.include_in_express === true);
    
    // Renumber express stops starting from 0
    expressStops = expressStops.map((stop, index) => {
      const hasExpressAudio = stop.express_audio_url && stop.express_audio_url.trim() !== '';
      
      console.log(`[TourPlayer] Stop ${index} "${stop.title}":`, {
        hasExpressAudio,
        express_audio_url: stop.express_audio_url,
        regular_audio_url: stop.audio_url,
        will_use: hasExpressAudio ? 'EXPRESS' : 'REGULAR'
      });
      
      return {
        ...stop,
        original_stop_number: stop.stop_number,
        stop_number: index,
        audio_url: hasExpressAudio ? stop.express_audio_url : stop.audio_url,
      };
    });
    
    console.log('[TourPlayer] Express tour loaded with', expressStops.length, 'stops');
    return expressStops;
  };
  
  // Fetch backend-saved progress for this tour. Silently returns null on
  // any error or if the user is unauthenticated — we always have the
  // localStorage fallback.
  const fetchBackendProgress = async (tourId) => {
    if (!tourId || !token) return null;
    try {
      const res = await axios.get(`${API}/tour-progress/${tourId}`, {
        headers: { Authorization: `Bearer ${token}` },
        timeout: 5000,
      });
      return res.data || null;
    } catch (_) {
      return null;
    }
  };

  // Push resume state to the backend. Fire-and-forget — errors are swallowed;
  // local state is authoritative for offline resilience. Throttled by the
  // caller (see the timeupdate handler).
  const pushBackendProgress = (patch) => {
    const tourId = purchase?.tour?.tour_id;
    if (!tourId || !token) return;
    axios.put(
      `${API}/tour-progress/${tourId}`,
      {
        stop_id: patch.stop_id || null,
        stop_index: patch.stop_index || 0,
        current_time: patch.current_time || 0,
        version: patch.version || null,
        completed: !!patch.completed,
      },
      { headers: { Authorization: `Bearer ${token}` }, timeout: 5000 },
    ).catch(() => { /* silent */ });
  };

  const clearBackendProgress = () => {
    const tourId = purchase?.tour?.tour_id;
    if (!tourId || !token) return;
    axios.delete(`${API}/tour-progress/${tourId}`, {
      headers: { Authorization: `Bearer ${token}` },
      timeout: 5000,
    }).catch(() => { /* silent */ });
  };

  // Read + validate the saved resume state for the current purchase.
  // Returns null when no state exists, the tour was completed, the saved
  // version doesn't match the requested one, or the saved stop_id is no
  // longer in the tour (e.g., admin deleted the stop).
  //
  // Cross-device: if `backendState` is provided (from GET /tour-progress),
  // we pick whichever of local vs backend has the more recent `updated_at`
  // and validate that one.
  const readResume = (version, stopsToCheck, backendState = null) => {
    if (!resumeStorageKey) return null;
    try {
      let saved = null;
      const raw = localStorage.getItem(resumeStorageKey);
      const local = raw ? JSON.parse(raw) : null;
      const localTs = local?.updated_at ? Number(local.updated_at) || 0 : 0;
      // Backend timestamps are ISO strings; convert.
      const backendTs = backendState?.updated_at ? Date.parse(backendState.updated_at) || 0 : 0;
      if (local && (!backendState || localTs >= backendTs)) {
        saved = local;
      } else if (backendState) {
        saved = {
          stop_index: backendState.stop_index,
          stop_id: backendState.stop_id,
          current_time: backendState.current_time,
          version: backendState.version,
          completed: !!backendState.completed,
          updated_at: backendTs || Date.now(),
        };
      }
      if (!saved || saved.completed) return null;
      if (saved.version && version && saved.version !== version) return null;
      // Prefer matching by stop_id (survives reorderings); fall back to index.
      let idx = -1;
      if (saved.stop_id) {
        idx = stopsToCheck.findIndex((s) => s?.stop_id === saved.stop_id);
      }
      if (idx < 0 && Number.isInteger(saved.stop_index)) {
        idx = saved.stop_index;
      }
      if (idx < 0 || idx >= stopsToCheck.length) return null;
      // Skip resume if the user only got as far as stop 0 (the intro) —
      // that's basically "never started", so honor the normal intro flow.
      const savedStop = stopsToCheck[idx];
      const barelyStarted = idx === 0 && (savedStop?.is_intro || savedStop?.stop_number === 0) && (saved.current_time || 0) < 8;
      if (barelyStarted) return null;
      return {
        stopIndex: idx,
        currentTime: Math.max(0, saved.current_time || 0),
        stop: savedStop,
      };
    } catch (_) {
      return null;
    }
  };

  // Manually start over. Clears the saved resume state and reloads at stop 0.
  const restartTour = () => {
    try {
      if (resumeStorageKey) localStorage.removeItem(resumeStorageKey);
    } catch (_) { /* noop */ }
    clearBackendProgress();
    resumeAppliedRef.current = false;
    savedResumeTimeRef.current = null;
    setCurrentStopIndex(0);
    // Fire the intro from the top so the user gets the fresh start they asked for.
    const first = stopsRef.current[0];
    if (first && playStopAudioRef.current) {
      // Reset audio state before firing the new stop
      const audio = persistentAudioRef.current;
      if (audio) {
        try { audio.pause(); audio.currentTime = 0; } catch (_) { /* noop */ }
      }
      playStopAudioRef.current(first);
    }
    toast.success('Restarted from the beginning');
  };

  // Handle version selection
  const handleVersionSelect = async (version) => {
    unlockAudio();
    setSelectedVersion(version);
    setShowVersionChoice(false);
    
    // If test mode is active, refetch stops with test map coords for this version
    if (testMode && selectedTestMap) {
      await refetchStopsWithTestMap(selectedTestMap.map_id);
      const currentStops = stopsRef.current;
      if (version === 'express') {
        const expressStops = filterForExpress(currentStops);
        setStops(expressStops);
        stopsRef.current = expressStops;
      }
      // Resume support — see main fetch path for logic comments.
      const testBackend = await fetchBackendProgress(purchase?.tour?.tour_id);
      const testResume = readResume(version, stopsRef.current, testBackend);
      if (testResume) {
        resumeAppliedRef.current = true;
        savedResumeTimeRef.current = testResume.currentTime;
        setCurrentStopIndex(testResume.stopIndex);
        if (playStopAudioRef.current && testResume.stop) {
          playStopAudioRef.current(testResume.stop);
        }
        toast.success(
          `Resumed at Stop ${testResume.stopIndex + 1}: ${testResume.stop?.title || ''}`,
          { duration: 8000, action: { label: 'Start over', onClick: () => restartTour() } },
        );
        return;
      }
      // Auto-play intro SYNCHRONOUSLY in the user gesture so iOS allows it.
      // (A setTimeout here previously broke the gesture chain and forced a manual tap.)
      const stopsToUse = stopsRef.current;
      if (stopsToUse.length > 0 && (stopsToUse[0].is_intro || stopsToUse[0].stop_number === 0)) {
        if (playStopAudioRef.current && stopsToUse[0]) {
          playStopAudioRef.current(stopsToUse[0]);
          toast.success('Tour started! Listen to the introduction.');
        }
      }
      return;
    }
    
    let stopsToUse;
    if (version === 'express') {
      stopsToUse = filterForExpress(allStops);
      setStops(stopsToUse);
    } else {
      stopsToUse = allStops;
      setStops(stopsToUse);
    }
    
    // Resume from where the user left off, if applicable. Backend fetch
    // in parallel with local check so cross-device users get a seamless
    // pickup on a new phone.
    const backendProgress = await fetchBackendProgress(purchase?.tour?.tour_id);
    const resume = readResume(version, stopsToUse, backendProgress);
    if (resume) {
      resumeAppliedRef.current = true;
      savedResumeTimeRef.current = resume.currentTime;
      setCurrentStopIndex(resume.stopIndex);
      if (playStopAudioRef.current && resume.stop) {
        playStopAudioRef.current(resume.stop);
      }
      toast.success(
        `Resumed at Stop ${resume.stopIndex + 1}: ${resume.stop?.title || ''}`,
        { duration: 8000, action: { label: 'Start over', onClick: () => restartTour() } },
      );
      return;
    }

    // Auto-play intro SYNCHRONOUSLY (preserve user gesture for iOS autoplay)
    if (stopsToUse.length > 0 && (stopsToUse[0].is_intro || stopsToUse[0].stop_number === 0)) {
      if (playStopAudioRef.current && stopsToUse[0]) {
        playStopAudioRef.current(stopsToUse[0]);
        toast.success('Tour started! Listen to the introduction.');
      }
    }
  };
  
  // Start the tour after viewing the map
  const handleStartTourFromMap = () => {
    unlockAudio(); // Unlock audio on this user gesture
    setShowFullTourMap(false);
    // Show intro prompt after closing map
    const stopsToUse = selectedVersion === 'express' ? filterForExpress(allStops) : allStops;
    if (stopsToUse.length > 0 && (stopsToUse[0].is_intro || stopsToUse[0].stop_number === 0)) {
      setShowIntroPrompt(true);
    }
  };

  const startGPSTracking = () => {
    if (!('geolocation' in navigator)) {
      toast.error('GPS not supported on this device');
      return;
    }

    // iOS Safari + dense urban areas (stone buildings, narrow alleys like Akko's
    // old city) often time out when enableHighAccuracy:true. We try high-accuracy
    // first, and if we get a TIMEOUT or POSITION_UNAVAILABLE we silently fall
    // back to standard-accuracy tracking instead of showing a dead-end error.
    let watchId = null;
    let fellBackToLowAccuracy = false;

    const onPosition = (position) => {
      const loc = { lat: position.coords.latitude, lng: position.coords.longitude };
      const accuracy = position.coords.accuracy;
      setUserLocation(loc);
      userLocationRef.current = loc;

      // Accumulate walked distance from consecutive accepted fixes. We do
      // this here (inside the geolocation callback) so it keeps running
      // even when the screen sleeps — same reason we run the geofence check
      // here on iOS via the Silent Bridge.
      const sample = { lat: loc.lat, lng: loc.lng, accuracy_m: accuracy };
      const delta = acceptedDeltaMeters(lastAcceptedFixRef.current, sample);
      if (delta > 0) {
        walkedMetersRef.current += delta;
      }
      // Only advance the "last accepted" pointer for fixes we trust —
      // otherwise a single bad fix could poison the next diff.
      if (accuracy == null || accuracy <= 30) {
        lastAcceptedFixRef.current = sample;
      }
      // Anchor the walking-time clock the first time we get a real fix.
      if (walkStartTsRef.current == null) {
        walkStartTsRef.current = Date.now();
      }

      // Background-survival trigger: iOS keeps firing watchPosition callbacks
      // while the audio session is alive (Silent Bridge), even when the screen
      // sleeps. Running the proximity check here — not just on a setInterval —
      // is what makes geofences fire with the phone in your pocket.
      try { checkProximityRef.current?.('watch'); } catch (_) { /* never let a check error kill GPS */ }
    };

    const onError = (error) => {
      // Permission denied is terminal — surface a clear message and stop trying.
      if (error.code === 1 /* PERMISSION_DENIED */) {
        toast.error('Location permission denied. Enable it in Settings → Safari → Location.');
        return;
      }

      // For TIMEOUT (3) or POSITION_UNAVAILABLE (2): try low-accuracy ONCE,
      // then if THAT also fails surface a helpful message.
      if (!fellBackToLowAccuracy && (error.code === 2 || error.code === 3)) {
        fellBackToLowAccuracy = true;
        try { if (watchId !== null) navigator.geolocation.clearWatch(watchId); } catch (_) { /* noop */ }
        toast.message('Switching to standard-accuracy GPS for better coverage here…', { duration: 3000 });
        watchId = navigator.geolocation.watchPosition(
          onPosition,
          (e2) => {
            // Final fallback — show the actual reason so the user can act.
            const reason = e2.code === 1 ? 'permission denied'
              : e2.code === 2 ? 'no GPS signal (try moving to open sky)'
              : e2.code === 3 ? 'GPS request timed out'
              : 'unknown error';
            toast.error(`Location unavailable: ${reason}.`);
          },
          { enableHighAccuracy: false, maximumAge: 5000, timeout: 25000 }
        );
        return;
      }

      // Any other case (already on fallback, or unknown error code)
      toast.error('GPS tracking error. Please ensure location services are on.');
    };

    watchId = navigator.geolocation.watchPosition(
      onPosition,
      onError,
      { enableHighAccuracy: true, maximumAge: 2000, timeout: 10000 }
    );
  };

  // Tiny silent MP3 used to keep the audio session alive between stops
  // (so iOS allows .play() from the proximity setInterval).
  const SILENT_AUDIO_DATA_URI =
    'data:audio/mp3;base64,SUQzBAAAAAAAI1RTU0UAAAAPAAADTGF2ZjU4Ljc2LjEwMAAAAAAAAAAAAAAA//tQAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAWGluZwAAAA8AAAACAAABhgC7u7u7u7u7u7u7u7u7u7u7u7u7u7u7u7u7u7u7u7u7u7u7u7u7u7u7u7u7u7u7u7u7u7u7//////////////////////////////////////////////////////////////////8AAAAATGF2YzU4LjEzAAAAAAAAAAAAAAAAJAAAAAAAAAAAAYYoRBqpAAAAAAD/+1DEAAAH+AV/UAAAIAAAA0gAAABMQU1FMy4xMDBVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVV//tQxBmAAADSAAAAAAAAANIAAAAAVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVV';

  // Start a silent muted loop on the persistent audio element between stops.
  // This keeps the iOS audio session alive so the proximity setInterval can
  // later swap .src and call .play() without being blocked.
  const startSilentBridge = () => {
    const audioEl = persistentAudioRef.current;
    if (!audioEl) return;
    try {
      audioEl.loop = true;
      audioEl.muted = true;
      audioEl.src = SILENT_AUDIO_DATA_URI;
      audioEl.currentTime = 0;
      playbackStateRef.current = { mode: 'silent-bridge' };
      const p = audioEl.play();
      if (p && typeof p.catch === 'function') {
        p.catch((e) => {
          console.log('[Audio] Silent bridge blocked:', e?.message);
          // If iOS rejects the bridge, surface a resume banner so the user
          // can re-arm the session with one tap.
          setNeedsResume(true);
        });
      }
      console.log('[Audio] Silent bridge started');
    } catch (e) {
      console.log('[Audio] Silent bridge error:', e);
      setNeedsResume(true);
    }
  };

  // Stop the silent bridge — called at the top of playStopAudio so the real
  // audio is not muted or looped.
  const stopSilentBridge = () => {
    const audioEl = persistentAudioRef.current;
    if (!audioEl) return;
    audioEl.loop = false;
    audioEl.muted = false;
  };

  // Create and activate a persistent Audio element on user gesture.
  // iOS requires Audio elements to be first played from a user tap.
  // Once activated, we reuse this SAME element for all stops.
  const unlockAudio = () => {
    if (persistentAudioRef.current) return; // Already created
    try {
      const audioEl = new Audio();
      // CRITICAL: assign the ref synchronously so any code path running
      // before the silent play() promise resolves can still find the same
      // unlocked element (prevents accidental `new Audio()` creation that
      // would not inherit the iOS user-gesture grace period).
      persistentAudioRef.current = audioEl;
      audioEl.src = SILENT_AUDIO_DATA_URI;
      const p = audioEl.play();
      if (p && typeof p.then === 'function') {
        p.then(() => {
          audioEl.pause();
          audioEl.currentTime = 0;
          console.log('[Audio] Persistent audio element activated via user gesture');
        }).catch(() => {
          // Even if silent play fails, the element is stored — iOS
          // sometimes still allows .play() on a previously-touched element.
          console.log('[Audio] Stored audio element (silent play blocked)');
        });
      }
    } catch (e) {
      console.log('[Audio] Failed to create audio element:', e);
    }
  };

  // Start the intro automatically
  const startIntro = () => {
    if (stops.length > 0) {
      const firstStop = stops[0];
      setShowIntroPrompt(false);
      setIntroPlayed(true);
      playStopAudio(firstStop);
      toast.success('Tour started! Listen to the introduction.');
    }
  };

  // Analytics tracking helper — fires silently, never blocks UI
  const trackEvent = (eventType, extra = {}) => {
    const email = user?.email;
    // Prefer an explicitly-passed tour_id (needed for tour_started, which
    // fires before React state has flushed). Fall back to purchase state.
    const tourId = extra.tour_id || purchase?.tour_id;
    if (!email || !tourId) return;

    const deviceType = /Mobile|Android|iPhone|iPad/.test(navigator.userAgent) ? 'mobile' : 'desktop';
    // Strip tour_id from extra so it doesn't collide in the payload spread.
    const { tour_id: _drop, ...restExtra } = extra;

    axios.post(`${API}/analytics/event`, {
      event_type: eventType,
      tour_id: tourId,
      session_id: analyticsSessionId,
      email: email,
      device_type: deviceType,
      ...restExtra
    }).then((res) => {
      // Backend echoes any Strides awarded by this event so we can pop a
      // real-time toast — full transparency on every earn.
      const awards = res?.data?.strides_awarded || [];
      awards.forEach((a) => {
        if (a && a.delta > 0) {
          toast.success(`+${a.delta} Strides`, {
            description: a.label || 'Audiostride reward',
            duration: 4000,
          });
        }
      });
      if (awards.length > 0) {
        window.dispatchEvent(new Event('strides:updated'));
      }
    }).catch(() => {}); // Silent fail
  };

  // Persist the "already fired" hidden-waypoint set to localStorage so
  // it survives backtracks and page reloads within the tour.
  const persistHiddenFired = () => {
    try {
      const tourId = purchase?.tour_id;
      if (!tourId) return;
      localStorage.setItem(
        `hidden_fired:${tourId}`,
        JSON.stringify(Array.from(hiddenFiredRef.current || []))
      );
    } catch (_) { /* silent */ }
  };

  // Play a hidden waypoint's audio without disturbing the tour sequence.
  // Reuses the persistentAudioRef (unlocked by the original user gesture)
  // so iOS lets it play; snapshots + restores primary state so the current
  // stop resumes exactly where it left off.
  //
  //   mode === 'warn'      -> INTERRUPT. Pauses primary, plays waypoint,
  //                          restores + resumes primary on ended.
  //   mode === 'encourage' -> QUEUE. Only fires when primary is idle
  //                          (paused / ended / not yet started). The
  //                          caller enforces this precondition.
  const playWaypointAudio = (waypoint) => {
    const audioEl = persistentAudioRef.current;
    if (!audioEl || !waypoint || !waypoint.audio_url) {
      // No audio to play — still show the toast (if any) and mark fired.
      if (waypoint?.toast_message) {
        const kind = waypoint.hidden_kind === 'warn' ? 'warning' : 'success';
        toast[kind === 'warning' ? 'warning' : 'success'](waypoint.toast_message, { duration: 6000 });
      }
      hiddenFiredRef.current.add(waypoint.stop_id);
      persistHiddenFired();
      return;
    }

    // Snapshot primary state so we can restore after the waypoint.
    const wasPlaying = !audioEl.paused && !audioEl.ended;
    primaryAudioSnapshotRef.current = {
      src: audioEl.src,
      currentTime: audioEl.currentTime || 0,
      wasPlaying,
      // Preserve handlers so we can re-attach them on restore
      onended: audioEl.onended,
      ontimeupdate: audioEl.ontimeupdate,
      onloadedmetadata: audioEl.onloadedmetadata,
      onerror: audioEl.onerror,
    };
    waypointPlayingRef.current = true;
    // Flag intentional pause so the interruption detector doesn't
    // mistake this for an iOS suspend.
    intentionalPauseRef.current = true;

    // Show the toast (green for encourage, amber for off-track). We use
    // `toast.warning` for off-track when available and fall back to a
    // custom style otherwise.
    if (waypoint.toast_message) {
      if (waypoint.hidden_kind === 'warn') {
        toast.warning?.(waypoint.toast_message, { duration: 8000 })
          ?? toast(waypoint.toast_message, { duration: 8000, className: 'bg-amber-100 text-amber-900' });
      } else {
        toast.success(waypoint.toast_message, { duration: 6000 });
      }
    }

    // Clear existing handlers so the primary onended (which calls
    // advanceToNextStop) doesn't fire on the WAYPOINT audio.
    audioEl.onended = null;
    audioEl.ontimeupdate = null;
    audioEl.onloadedmetadata = null;
    audioEl.onerror = null;

    try {
      audioEl.pause();
    } catch (_) { /* noop */ }

    // Swap src to waypoint audio and play. On ended (or on error) restore
    // primary state and resume if it was playing.
    const restorePrimary = () => {
      const snap = primaryAudioSnapshotRef.current;
      waypointPlayingRef.current = false;
      primaryAudioSnapshotRef.current = null;
      if (!snap) return;
      // Re-attach primary handlers first so subsequent events flow correctly
      audioEl.onended = snap.onended || null;
      audioEl.ontimeupdate = snap.ontimeupdate || null;
      audioEl.onloadedmetadata = snap.onloadedmetadata || null;
      audioEl.onerror = snap.onerror || null;
      // Restore src + position; only resume if primary was actively playing
      if (snap.src) {
        try {
          if (audioEl.src !== snap.src) audioEl.src = snap.src;
          audioEl.currentTime = snap.currentTime || 0;
        } catch (_) { /* noop */ }
        if (snap.wasPlaying) {
          intentionalPauseRef.current = true;
          const p = audioEl.play();
          if (p && typeof p.catch === 'function') {
            p.catch(() => {
              // If iOS refuses to auto-resume, surface the resume banner
              // so the user can re-arm with one tap.
              setNeedsResume(true);
            });
          }
        }
      }
    };

    audioEl.onended = restorePrimary;
    audioEl.onerror = () => {
      console.log('[Waypoint] audio error, restoring primary');
      restorePrimary();
    };

    try {
      audioEl.src = waypoint.audio_url;
      audioEl.currentTime = 0;
      const p = audioEl.play();
      if (p && typeof p.catch === 'function') {
        p.catch((e) => {
          console.log('[Waypoint] play() rejected:', e?.message);
          restorePrimary();
        });
      }
    } catch (e) {
      console.log('[Waypoint] play threw:', e);
      restorePrimary();
    }

    hiddenFiredRef.current.add(waypoint.stop_id);
    persistHiddenFired();
    trackEvent('hidden_waypoint_fired', {
      stop_id: waypoint.stop_id,
      stop_title: waypoint.title,
      hidden_kind: waypoint.hidden_kind || 'encourage',
    });
  };

  // Contextual eligibility helpers for hidden waypoints.
  //
  // "Armed" means the waypoint's parent stop has been completed (or it has
  // no parent) → the waypoint is now allowed to fire when the user enters
  // its GPS radius.
  //
  // "Expired" means the waypoint's window has closed (the user reached the
  // expected next stop, or a different stop, or the time budget ran out) →
  // never fires this session even if the user later walks through it.
  //
  // Both are session-scoped refs (not persisted) because the fire-once
  // guarantee is enforced separately by hiddenFiredRef + localStorage.
  const armWaypointsForParent = (parentStopNumber) => {
    const now = Date.now();
    const waypoints = hiddenWaypointsRef.current || [];
    for (const wp of waypoints) {
      if (!wp || !wp.stop_id) continue;
      if (wp.trigger_after_stop_number === parentStopNumber &&
          !waypointArmedAtRef.current.has(wp.stop_id) &&
          !expiredWaypointsRef.current.has(wp.stop_id)) {
        waypointArmedAtRef.current.set(wp.stop_id, now);
      }
    }
  };

  // Called when the user's audio finishes on a visible stop OR when they
  // enter the geofence of a visible stop (arrival is a slightly softer
  // signal than audio-completion but still ends the eligibility window).
  const closeWaypointWindowFor = (arrivedStopNumber) => {
    const waypoints = hiddenWaypointsRef.current || [];
    const armedIds = waypointArmedAtRef.current;
    for (const wp of waypoints) {
      if (!wp || !wp.stop_id) continue;
      if (!armedIds.has(wp.stop_id)) continue;
      if (hiddenFiredRef.current.has(wp.stop_id)) continue;
      if (expiredWaypointsRef.current.has(wp.stop_id)) continue;

      const expected = wp.expires_when_stop_number;
      const arrivedIsExpected = expected != null && expected === arrivedStopNumber;
      const arrivedIsSomethingElse = expected != null && expected !== arrivedStopNumber;

      if (arrivedIsExpected) {
        expiredWaypointsRef.current.add(wp.stop_id);
      } else if (arrivedIsSomethingElse) {
        // Wrong stop while armed — nudge the user back on route, then expire.
        expiredWaypointsRef.current.add(wp.stop_id);
        if (wp.recovery_message) {
          try {
            toast(wp.recovery_message, {
              duration: 8000,
              icon: '🧭',
            });
          } catch (_) { /* toast lib absent — silent */ }
        }
      }
    }
  };

  // Check every tick whether the user has walked into any hidden waypoint's
  // GPS radius. Called from within checkProximityRef right after the primary
  // stop check.
  //
  //  * "warn" (off-track): fires immediately, interrupting whatever is
  //    playing — safety-critical.
  //  * "encourage": fires only when nothing else is playing (natural queue).
  //    If primary audio is busy, we wait — the next tick will retry.
  //  * Priority: warn > encourage. If both are in range this tick, warn fires
  //    first; encourage will re-check next tick.
  //  * Each waypoint fires AT MOST once per tour session (persisted via
  //    hiddenFiredRef → localStorage).
  //  * Contextual eligibility: waypoints with `trigger_after_stop_number`
  //    must be ARMED (parent stop's audio finished). Waypoints with
  //    `expires_when_stop_number` or `expires_after_seconds` skip firing
  //    once expired (see closeWaypointWindowFor + the elapsed check below).
  const checkHiddenWaypoints = () => {
    const loc = userLocationRef.current;
    const waypoints = hiddenWaypointsRef.current || [];
    if (!loc || waypoints.length === 0) return;
    if (waypointPlayingRef.current) return; // don't stack waypoints
    const now = Date.now();

    // Build the two candidate lists in a single pass.
    let warnCandidate = null;
    let encourageCandidate = null;
    for (const wp of waypoints) {
      if (!wp || !wp.stop_id) continue;
      if (hiddenFiredRef.current.has(wp.stop_id)) continue;
      if (expiredWaypointsRef.current.has(wp.stop_id)) continue;

      // Contextual eligibility gate:
      // - If waypoint has a trigger_after_stop_number, must be armed.
      // - If time-based expiry is set and elapsed, mark expired + skip.
      const hasTrigger = wp.trigger_after_stop_number != null;
      const armedAt = waypointArmedAtRef.current.get(wp.stop_id);
      if (hasTrigger && armedAt == null) continue; // not armed yet
      if (armedAt != null && wp.expires_after_seconds &&
          (now - armedAt) > wp.expires_after_seconds * 1000) {
        expiredWaypointsRef.current.add(wp.stop_id);
        continue;
      }

      const lat = parseFloat(wp.latitude);
      const lng = parseFloat(wp.longitude);
      if (isNaN(lat) || isNaN(lng)) continue;
      const d = calculateDistance(loc.lat, loc.lng, lat, lng);
      const radius = wp.radius_meters || 50;
      if (d > radius) continue;
      if (wp.hidden_kind === 'warn') {
        if (!warnCandidate) warnCandidate = wp;
      } else if (!encourageCandidate) {
        encourageCandidate = wp;
      }
    }

    // Priority: warn > encourage
    if (warnCandidate) {
      playWaypointAudio(warnCandidate);
      return;
    }
    if (encourageCandidate) {
      // Only fire encouragement if primary audio isn't actively playing —
      // this is the "queue after current stop finishes" behavior.
      const audioEl = persistentAudioRef.current;
      const primaryBusy = !!audioEl && !audioEl.paused && !audioEl.ended && audioEl.currentTime > 0;
      if (!primaryBusy) {
        playWaypointAudio(encourageCandidate);
      }
      // else: try again on the next 2-second proximity tick.
    }
  };

  // Get navigation mode for current stop
  const getNavigationMode = () => {
    const currentStop = stops[currentStopIndex];
    if (currentStop?.navigation_mode === 'driving') {
      return 'driving';
    }
    return tourType === 'driving' ? 'driving' : 'walking';
  };

  const calculateDistance = (lat1, lon1, lat2, lon2) => {
    const R = 6371000;
    const dLat = ((lat2 - lat1) * Math.PI) / 180;
    const dLon = ((lon2 - lon1) * Math.PI) / 180;
    const a =
      Math.sin(dLat / 2) * Math.sin(dLat / 2) +
      Math.cos((lat1 * Math.PI) / 180) *
        Math.cos((lat2 * Math.PI) / 180) *
        Math.sin(dLon / 2) *
        Math.sin(dLon / 2);
    const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
    return R * c;
  };

  const playStopAudio = (stop) => {
    // Contextual hidden-waypoint bookkeeping: reaching a visible stop
    // closes the eligibility window of any hidden waypoint tied to a
    // previous stop. This is what makes "wrong turn" warnings expire once
    // the traveler arrives — no interference during loop-backs or dense
    // urban tours where paths cross.
    if (stop && !stop.is_hidden && !stop.is_intro && typeof stop.stop_number === 'number') {
      closeWaypointWindowFor(stop.stop_number);
    }

    // Make sure the silent bridge isn't muting/looping the real audio
    stopSilentBridge();

    // We're about to pause/replace; flag intentional so onpause doesn't
    // mistake it for an iOS interruption.
    intentionalPauseRef.current = true;

    // Stop any currently playing audio
    if (persistentAudioRef.current) {
      persistentAudioRef.current.pause();
    }
    if (audio && audio !== persistentAudioRef.current) {
      audio.pause();
    }
    if (audioIntervalRef.current) {
      clearInterval(audioIntervalRef.current);
    }
    setAudioCompleted(false);
    setAudioCurrentTime(0);
    setAudioDuration(0);
    setNeedsResume(false); // we're attempting fresh playback

    console.log(`[Audio] Playing stop "${stop.title}": url=${stop.audio_url ? 'YES' : 'NO'}`);

    // Track stop_played event (or intro_played for introductions)
    if (stop.is_intro) {
      trackEvent('intro_played', {
        stop_id: stop.stop_id,
        stop_number: stop.stop_number,
        stop_title: stop.title,
      });
    }
    trackEvent('stop_played', {
      stop_id: stop.stop_id,
      stop_number: stop.stop_number,
      stop_title: stop.title
    });
    if (stop.audio_url) {
      if (stop.audio_url.includes('soundcloud.com') && stop.timing) {
        const timeParts = stop.timing.split('-')[0].split(':');
        const startSeconds = timeParts.length === 2 
          ? parseInt(timeParts[0]) * 60 + parseInt(timeParts[1])
          : parseInt(timeParts[0]) * 3600 + parseInt(timeParts[1]) * 60 + parseInt(timeParts[2]);
        toast.success(`Playing: ${stop.title}`, { description: stop.timing, duration: 5000 });
        setPlaying(true);
      } else {
        // REUSE the persistent audio element (activated on first user tap)
        // If not yet created, create one now (will work if called from user gesture)
        let audioEl = persistentAudioRef.current;
        if (!audioEl) {
          audioEl = new Audio();
          persistentAudioRef.current = audioEl;
        }
        
        // Clear old event handlers
        audioEl.onloadedmetadata = null;
        audioEl.ontimeupdate = null;
        audioEl.onended = null;
        audioEl.onerror = null;
        
        // Set new source
        audioEl.src = stop.audio_url;
        audioEl.currentTime = 0;
        
        audioEl.onloadedmetadata = () => {
          setAudioDuration(audioEl.duration);
          // If we're resuming mid-stop, jump the audio to the saved position
          // once metadata is available (seek needs duration to be known).
          if (savedResumeTimeRef.current != null) {
            const target = savedResumeTimeRef.current;
            savedResumeTimeRef.current = null; // one-shot
            try {
              if (target > 0 && audioEl.duration && target < audioEl.duration - 2) {
                audioEl.currentTime = target;
                setAudioCurrentTime(target);
              }
            } catch (_) { /* silent */ }
          }
        };
        
        audioEl.ontimeupdate = () => {
          setAudioCurrentTime(audioEl.currentTime);
          // Persist audio position ~every 5s locally + ~every 30s to the
          // backend so a device swap picks up where the user left off.
          try {
            const now = Date.now();
            if (
              resumeStorageKey &&
              !waypointPlayingRef.current &&
              audioEl.currentTime > 0
            ) {
              const patch = {
                stop_index: currentStopIndexRef.current,
                stop_id: stop?.stop_id || null,
                current_time: Math.floor(audioEl.currentTime),
                version: selectedVersion,
                completed: false,
              };
              if (now - lastResumeSaveRef.current > 5000) {
                lastResumeSaveRef.current = now;
                const raw = localStorage.getItem(resumeStorageKey);
                const prev = raw ? JSON.parse(raw) : {};
                localStorage.setItem(resumeStorageKey, JSON.stringify({
                  ...prev,
                  ...patch,
                  updated_at: now,
                }));
              }
              if (now - lastBackendSaveRef.current > 30000) {
                lastBackendSaveRef.current = now;
                pushBackendProgress(patch);
              }
            }
          } catch (_) { /* silent */ }
        };
        
        audioEl.onended = () => {
          setPlaying(false);
          setAudioCompleted(true);
          setAudioCurrentTime(0);
          // Track that this visible stop's audio has fully played out —
          // this is the exact moment "after stop N" hidden waypoints
          // become eligible.
          if (typeof stop.stop_number === 'number') {
            completedVisibleStopsRef.current.add(stop.stop_number);
            armWaypointsForParent(stop.stop_number);
          }
          // Soft pulse the "Replay directions" button — the "okay, what
          // now?" ambient nudge. Only on non-final stops (final stop has
          // no directions button to pulse).
          const isFinalStop = currentStopIndexRef.current >= stopsRef.current.length - 1;
          if (!isFinalStop) {
            setDirectionsPulse(true);
            setTimeout(() => setDirectionsPulse(false), 6000);
          }
          // Track stop completion
          trackEvent('stop_completed', {
            stop_id: stop.stop_id,
            stop_number: stop.stop_number,
            stop_title: stop.title,
            listen_duration: audioEl.duration || 0
          });
          advanceToNextStop();
        };
        
        audioEl.onerror = (e) => {
          console.log(`[Audio] Error loading "${stop.title}":`, e);
          toast.error(`Audio unavailable for: ${stop.title}`);
          setAudioCompleted(true);
        };

        // Interruption detector: if the audio pauses while we still expect
        // it to be playing (e.g. iOS suspended us due to phone call,
        // long screen lock, or another app taking the audio session),
        // surface the resume banner.
        audioEl.onpause = () => {
          if (intentionalPauseRef.current) {
            intentionalPauseRef.current = false;
            return;
          }
          // Mid-track + not flagged → real interruption
          if (
            audioEl.currentTime > 0 &&
            audioEl.duration &&
            audioEl.currentTime < audioEl.duration - 0.5
          ) {
            console.log('[Audio] Interruption detected mid-stop — needs resume');
            setNeedsResume(true);
          }
        };

        const playPromise = audioEl.play();
        playbackStateRef.current = { mode: 'playing-stop', stopId: stop.stop_id };
        if (playPromise) {
          playPromise.then(() => {
            console.log(`[Audio] Now playing: ${stop.title}`);
          }).catch((err) => {
            console.log(`[Audio] Play blocked for "${stop.title}":`, err.message);
            // iOS rejected → surface resume banner instead of silent failure.
            setNeedsResume(true);
            // Last resort: also re-arm on next user gesture anywhere on the page
            const resumePlay = () => {
              audioEl.play().then(() => setNeedsResume(false)).catch(() => {});
              document.removeEventListener('touchstart', resumePlay);
              document.removeEventListener('click', resumePlay);
            };
            document.addEventListener('touchstart', resumePlay, { once: true });
            document.addEventListener('click', resumePlay, { once: true });
          });
        }
        
        setAudio(audioEl);
        setPlaying(true);
      }
    } else {
      setAudioCompleted(true);
    }
  };
  
  // Resume playback after an iOS interruption (call, screen-lock,
  // backgrounded tab, audio session lost to another app).
  // Called either from the resume banner tap or the visibilitychange handler.
  const tryResume = () => {
    const audioEl = persistentAudioRef.current;
    if (!audioEl) {
      setNeedsResume(false);
      return;
    }
    const state = playbackStateRef.current;

    if (state.mode === 'silent-bridge') {
      // Just re-arm the muted loop
      try {
        audioEl.loop = true;
        audioEl.muted = true;
        if (audioEl.src !== SILENT_AUDIO_DATA_URI) {
          audioEl.src = SILENT_AUDIO_DATA_URI;
        }
        const p = audioEl.play();
        if (p && typeof p.then === 'function') {
          p.then(() => setNeedsResume(false)).catch(() => setNeedsResume(true));
        }
      } catch {
        setNeedsResume(true);
      }
      return;
    }

    if (state.mode === 'playing-stop') {
      // Resume the current stop from where it was — just call play() again.
      const p = audioEl.play();
      if (p && typeof p.then === 'function') {
        p.then(() => setNeedsResume(false)).catch(() => setNeedsResume(true));
      } else {
        setNeedsResume(false);
      }
      return;
    }

    setNeedsResume(false);
  };

  // Auto-attempt a resume whenever the tab becomes visible again
  // (covers the "user backgrounded the tab and came back" case).
  useEffect(() => {
    const handleVisibility = () => {
      if (document.visibilityState !== 'visible') return;
      const audioEl = persistentAudioRef.current;
      if (!audioEl) return;
      const state = playbackStateRef.current;
      // If we expected audio to be playing but the element is paused,
      // try to silently resume; if iOS rejects, the banner stays up.
      if (state.mode !== 'idle' && audioEl.paused) {
        const p = audioEl.play();
        if (p && typeof p.then === 'function') {
          p.then(() => setNeedsResume(false))
           .catch(() => setNeedsResume(true));
        }
      }
    };
    document.addEventListener('visibilitychange', handleVisibility);
    return () => document.removeEventListener('visibilitychange', handleVisibility);
  }, []);

  // Keep ref in sync so interval can call latest version
  playStopAudioRef.current = playStopAudio;
  
  // Advance to the next stop (called after audio completes or user skips)
  const advanceToNextStop = () => {
    const idx = currentStopIndexRef.current;
    const currentStops = stopsRef.current;
    
    if (idx < currentStops.length - 1) {
      const nextIdx = idx + 1;
      const nextStop = currentStops[nextIdx];
      setCurrentStopIndex(nextIdx);
      currentStopIndexRef.current = nextIdx;
      audioTriggeredRef.current = false;
      setAudioCompleted(false);
      
      // Show toast with next stop name
      if (nextStop && !nextStop.is_intro) {
        const mode = nextStop.navigation_mode === 'driving' ? 'Drive' : 
                     (tourType === 'driving' ? 'Drive' : 'Walk');
        toast.info(`Next: ${nextStop.title}`, {
          description: `${mode} to this stop — tap Navigate if you need directions`,
          duration: 6000
        });
        
        // IMMEDIATELY check if user is already at the next stop
        // This fires while still in the audio session chain (onended),
        // so iOS allows the .play() call
        const loc = userLocationRef.current;
        if (loc && nextStop.latitude && nextStop.longitude) {
          const lat = parseFloat(nextStop.latitude);
          const lng = parseFloat(nextStop.longitude);
          if (!isNaN(lat) && !isNaN(lng)) {
            const R = 6371000;
            const dLat = ((lat - loc.lat) * Math.PI) / 180;
            const dLon = ((lng - loc.lng) * Math.PI) / 180;
            const a = Math.sin(dLat / 2) * Math.sin(dLat / 2) +
              Math.cos((loc.lat * Math.PI) / 180) * Math.cos((lat * Math.PI) / 180) *
              Math.sin(dLon / 2) * Math.sin(dLon / 2);
            const distance = R * 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
            const radius = Math.max(nextStop.radius_meters || 50, 30);
            
            console.log(`[Advance] Immediate check: ${Math.round(distance)}m / ${radius}m to "${nextStop.title}"`);
            
            if (distance <= radius) {
              console.log(`[Advance] Already at "${nextStop.title}" — playing immediately`);
              audioTriggeredRef.current = true;
              if (playStopAudioRef.current) {
                playStopAudioRef.current(nextStop);
              }
              return;
            }
          }
        }

        // User isn't at the next stop yet → start silent bridge to keep the
        // iOS audio session alive so the proximity setInterval can autoplay
        // the next stop without requiring a tap.
        startSilentBridge();
      }
    } else {
      // Track tour completion
      trackEvent('tour_completed', {
        metadata: { session_duration: (Date.now() - parseInt(analyticsSessionId.split('-')[1])) / 1000 }
      });
      setTourCompleted(true);
      setNewsletterEmail(user?.email || '');
      setNewsletterName(user?.first_name ? `${user.first_name} ${user.last_name || ''}`.trim() : '');
      // Clear resume state — tour is finished. This clears both the
      // HomePage "Resume" banner key AND the per-purchase player-level
      // resume key so the next visit starts from the beginning.
      try { localStorage.removeItem('audiostride_last_tour'); } catch (_) { /* noop */ }
      try {
        if (resumeStorageKey) {
          // Mark completed rather than delete — lets us tell "finished, so
          // start over" apart from "never started" if we ever need to.
          localStorage.setItem(resumeStorageKey, JSON.stringify({
            stop_index: 0,
            stop_id: null,
            current_time: 0,
            version: selectedVersion,
            completed: true,
            updated_at: Date.now(),
          }));
        }
      } catch (_) { /* noop */ }
      // Mirror to backend so a device swap after completion also starts fresh.
      pushBackendProgress({
        stop_index: 0,
        stop_id: null,
        current_time: 0,
        version: selectedVersion,
        completed: true,
      });
      toast.success('Tour completed! 🎉');
      // Fetch any Strides + achievements just earned (best-effort)
      (async () => {
        try {
          const headers = { Authorization: `Bearer ${token}` };
          const [txRes, achRes] = await Promise.all([
            axios.get(`${API}/strides/transactions?limit=5`, { headers }),
            axios.get(`${API}/achievements/me`, { headers }),
          ]);
          // Sum positive deltas from the last 5 transactions tagged with this tour
          const tourId = purchase?.tour?.tour_id;
          const earned = (txRes.data?.transactions || [])
            .filter((t) => t.metadata?.tour_id === tourId && t.delta > 0)
            .reduce((sum, t) => sum + t.delta, 0);
          setStridesEarnedThisSession(earned);
          // Notify any mounted StridesBalance widget
          window.dispatchEvent(new Event('strides:updated'));
          // Surface most recently unlocked achievements (last 10 min)
          const cutoff = Date.now() - 10 * 60 * 1000;
          const recent = (achRes.data?.unlocked || [])
            .filter((a) => a.unlocked_at && new Date(a.unlocked_at).getTime() > cutoff);
          setNewlyUnlockedAchievements(recent);
        } catch (_) { /* best-effort */ }
      })();
    }
  };

  // Calculate distance between two points in meters
  const calculateDistanceMeters = (lat1, lon1, lat2, lon2) => {
    const R = 6371000; // Earth's radius in meters
    const dLat = ((lat2 - lat1) * Math.PI) / 180;
    const dLon = ((lon2 - lon1) * Math.PI) / 180;
    const a =
      Math.sin(dLat / 2) * Math.sin(dLat / 2) +
      Math.cos((lat1 * Math.PI) / 180) *
        Math.cos((lat2 * Math.PI) / 180) *
        Math.sin(dLon / 2) *
        Math.sin(dLon / 2);
    const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
    return R * c;
  };

  // Estimate travel time
  const getEstimatedTime = (distanceMeters, mode) => {
    // Average speeds: walking ~5km/h, driving ~30km/h (city traffic)
    const speedKmh = mode === 'driving' ? 30 : 5;
    const timeHours = (distanceMeters / 1000) / speedKmh;
    const timeMinutes = Math.round(timeHours * 60);
    
    if (timeMinutes < 1) return 'Less than 1 min';
    if (timeMinutes < 60) return `~${timeMinutes} min`;
    const hours = Math.floor(timeMinutes / 60);
    const mins = timeMinutes % 60;
    return `~${hours}h ${mins}m`;
  };

  // Get distance to next stop
  const getDistanceToStop = (stop) => {
    if (!userLocation || !stop?.latitude || !stop?.longitude) return null;
    return calculateDistanceMeters(userLocation.lat, userLocation.lng, parseFloat(stop.latitude), parseFloat(stop.longitude));
  };

  // Called when the ShareWalkCard reports a successful native-share.
  // Posts back to award Strides for share_tour (rate-limited server-side).
  const handleSocialShared = async () => {
    try {
      await axios.post(
        `${API}/social/share-completed`,
        { tour_id: purchase?.tour?.tour_id, channel: 'native_share' },
        { headers: { Authorization: `Bearer ${token}` } },
      );
      window.dispatchEvent(new Event('strides:updated'));
    } catch (_) { /* best-effort */ }
  };

  // Navigate to stop with chosen mode (uses preferred nav app)
  const navigateToStopWithMode = (stop, mode) => {
    if (preferredNavApp === 'waze') {
      const url = `https://waze.com/ul?ll=${stop.latitude},${stop.longitude}&navigate=yes`;
      window.open(url, '_blank');
    } else {
      const travelMode = mode === 'driving' ? 'driving' : 'walking';
      const url = `https://www.google.com/maps/dir/?api=1&destination=${stop.latitude},${stop.longitude}&travelmode=${travelMode}`;
      window.open(url, '_blank');
    }
    setShowNavigationChoice(false);
  };

  // Skip current stop (manual user action)
  const skipCurrentStop = () => {
    const currentStop = stops[currentStopIndex];
    if (currentStop) {
      trackEvent('stop_skipped', {
        stop_id: currentStop.stop_id,
        stop_number: currentStop.stop_number,
        stop_title: currentStop.title,
      });
    }
    if (audio) {
      audio.pause();
      setAudio(null);
    }
    setPlaying(false);
    toast.info(`Skipped: ${stops[currentStopIndex]?.title}`);
    advanceToNextStop();
  };

  // Manually play a specific stop (from the stop list)
  const playSpecificStop = (index) => {
    if (audio) {
      audio.pause();
    }
    setCurrentStopIndex(index);
    audioTriggeredRef.current = false;
    setAudioCompleted(false);
    playStopAudio(stops[index]);
    setShowStopsList(false);
  };

  const togglePlayPause = () => {
    if (!audio) {
      // If no audio loaded, manually play current stop
      playStopAudio(currentStop);
      return;
    }

    if (playing) {
      audio.pause();
      setPlaying(false);
    } else {
      audio.play();
      setPlaying(true);
    }
  };

  // Manual play button - allows user to play current stop without GPS trigger
  const playCurrentStopManually = () => {
    if (currentStop) {
      playStopAudio(currentStop);
    }
  };

  // Rewind 10 seconds
  const rewind10 = () => {
    if (audio) {
      audio.currentTime = Math.max(0, audio.currentTime - 10);
      setAudioCurrentTime(audio.currentTime);
    }
  };

  // Fast forward 10 seconds
  const forward10 = () => {
    if (audio) {
      audio.currentTime = Math.min(audio.duration, audio.currentTime + 10);
      setAudioCurrentTime(audio.currentTime);
    }
  };

  // Jump to the moment in the current stop's audio where the narrator
  // starts giving directions to the next stop. Robust against stale
  // audio state (duration === 0, src mismatched, ended, etc) — when
  // that happens we reload the current stop's audio first, then seek.
  const replayDirections = () => {
    setDirectionsPulse(false);
    let audioEl = audio || persistentAudioRef.current;
    const expectedSrc = currentStop?.audio_url || '';
    const srcLoaded = audioEl && audioEl.src && expectedSrc && audioEl.src.includes(expectedSrc.split('?')[0].split('/').pop());
    const durationOk = audioEl && Number.isFinite(audioEl.duration) && audioEl.duration > 0;

    console.log('[Replay Directions] click', {
      hasAudioEl: !!audioEl,
      audioElSrc: audioEl?.src?.slice(-60),
      expectedSrc: expectedSrc?.slice(-60),
      srcLoaded,
      duration: audioEl?.duration,
      durationOk,
      currentTime: audioEl?.currentTime,
      paused: audioEl?.paused,
      explicit: currentStop?.directions_start_seconds,
      stopTitle: currentStop?.title,
    });

    // STALE-STATE FIX: if audio has 0 duration or its src doesn't match
    // the current stop, reload from scratch via playStopAudio, then seek
    // once metadata is ready. This handles the case where audio ended,
    // got cleared between stops, or never loaded successfully.
    if (!audioEl || !durationOk || !srcLoaded) {
      console.log('[Replay Directions] stale audio — reloading current stop');
      toast.info('Loading audio…');
      if (currentStop) {
        playStopAudio(currentStop);
      }
      // playStopAudio sets persistentAudioRef.current synchronously. Wait
      // for the new element's metadata before seeking — the loadedmetadata
      // event is the iOS-reliable signal that duration is real.
      const tryAfterLoad = (attempt = 0) => {
        const el = persistentAudioRef.current;
        if (el && Number.isFinite(el.duration) && el.duration > 0) {
          doSeekToDirections(el);
          return;
        }
        if (el && attempt === 0) {
          const onMeta = () => {
            el.removeEventListener('loadedmetadata', onMeta);
            doSeekToDirections(el);
          };
          el.addEventListener('loadedmetadata', onMeta, { once: true });
        }
        if (attempt < 10) {
          setTimeout(() => tryAfterLoad(attempt + 1), 250);
        } else {
          toast.error('Could not load audio — tap play first');
        }
      };
      setTimeout(() => tryAfterLoad(0), 250);
      return;
    }
    doSeekToDirections(audioEl);
  };

  // Internal seek helper — pause first (iOS won't honor `currentTime`
  // changes mid-stream reliably), reposition, then resume.
  const doSeekToDirections = (audioEl) => {
    if (!audioEl) {
      console.log('[Replay Directions] no audio element to seek');
      toast.error('Audio not ready — try again');
      return;
    }
    // Pick the right timecode based on which audio version is playing.
    // Express audio and regular audio are different files with different
    // narration timing, so they need separate replay timecodes.
    const isExpressPlaying = selectedVersion === 'express'
      && currentStop?.express_audio_url
      && currentStop.express_audio_url.trim() !== '';
    const explicit = isExpressPlaying
      ? currentStop?.express_directions_start_seconds
      : currentStop?.directions_start_seconds;
    const duration = audioEl.duration || 0;
    if (!Number.isFinite(duration) || duration <= 0) {
      console.log('[Replay Directions] duration still 0 — bail');
      toast.error('Audio not loaded yet — tap play, then try again');
      return;
    }
    const fallback = Math.max(0, duration - 20);
    let target = (typeof explicit === 'number' && explicit >= 0 && explicit < duration)
      ? explicit : fallback;
    // Clamp just in case
    target = Math.max(0, Math.min(duration - 0.1, target));
    console.log('[Replay Directions] seeking to', target, 'of', duration);
    try {
      audioEl.pause();
      audioEl.currentTime = target;
      setAudioCurrentTime(target);
      setTimeout(() => {
        try {
          const p = audioEl.play();
          if (p?.catch) p.catch((err) => {
            console.log('[Replay Directions] play() rejected', err);
            toast.error('Could not start audio — tap play again');
          });
          setPlaying(true);
          const mm = Math.floor(target / 60);
          const ss = String(Math.floor(target % 60)).padStart(2, '0');
          toast.success(`Replaying directions from ${mm}:${ss}`);
        } catch (e) {
          console.log('[Replay Directions] play threw', e);
        }
      }, 80);
    } catch (err) {
      console.log('[Replay Directions] seek failed', err);
      toast.error('Could not jump — try again');
    }
  };

  // Open navigation with preferred app
  const openNavigation = (lat, lng, mode = 'walking') => {
    if (preferredNavApp === 'waze') {
      const url = `https://waze.com/ul?ll=${lat},${lng}&navigate=yes`;
      window.open(url, '_blank');
    } else {
      const travelMode = mode === 'driving' ? 'driving' : 'walking';
      const url = `https://www.google.com/maps/dir/?api=1&destination=${lat},${lng}&travelmode=${travelMode}`;
      window.open(url, '_blank');
    }
  };

  const openGoogleMaps = (lat, lng, title) => {
    const mode = getNavigationMode();
    openNavigation(lat, lng, mode);
  };

  const openWaze = (lat, lng, title) => {
    const url = `https://waze.com/ul?ll=${lat},${lng}&navigate=yes`;
    window.open(url, '_blank');
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-16 w-16 border-4 border-teal-500 border-t-transparent"></div>
      </div>
    );
  }

  // Safety net: if loading finished but we have no purchase or stops, render a
  // friendly fallback instead of a blank screen. Most common cause is admins
  // creating a tour with no stops yet, or the route loading before data arrives.
  if (!purchase) {
    return (
      <div className="min-h-screen bg-slate-950 text-white flex items-center justify-center p-6">
        <div className="max-w-sm text-center" data-testid="player-no-purchase">
          <p className="font-black text-2xl mb-2">Tour not loaded</p>
          <p className="text-white/70 text-sm mb-6">We couldn't find your access for this tour. Please go back and tap "Begin Experience" again.</p>
          <button onClick={() => navigate('/')} className="px-6 py-3 rounded-full bg-white text-slate-950 font-black">Back to Tours</button>
        </div>
      </div>
    );
  }

  if (!stops || stops.length === 0) {
    return (
      <div className="min-h-screen bg-slate-950 text-white flex items-center justify-center p-6">
        <div className="max-w-sm text-center" data-testid="player-no-stops">
          <p className="font-black text-2xl mb-2">No stops yet</p>
          <p className="text-white/70 text-sm mb-6">This tour doesn't have any stops added to it. Add at least one stop in the admin panel before walking the route.</p>
          <button onClick={() => navigate('/')} className="px-6 py-3 rounded-full bg-white text-slate-950 font-black">Back to Tours</button>
        </div>
      </div>
    );
  }

  const currentStop = stops[currentStopIndex];
  const stopsWithCoords = stops.filter(s => s.latitude && s.longitude);
  const mapCenter = userLocation || (stopsWithCoords.length > 0 ? { lat: parseFloat(stopsWithCoords[0].latitude), lng: parseFloat(stopsWithCoords[0].longitude) } : null);
  const isFirstStopIntro = stops.length > 0 && (stops[0].is_intro || stops[0].title?.toLowerCase().includes('intro'));

  // Starting-point marker: the first GPS-marked stop (skips intros).
  // Users need to physically walk here to begin the tour proper.
  const startingStopIndex = stops.findIndex(
    (s) => !s.is_intro && s.latitude && s.longitude,
  );
  const startingStop = startingStopIndex >= 0 ? stops[startingStopIndex] : null;
  // Show the starting-point banner until the user has advanced past this
  // stop OR is within its radius (i.e., has arrived and can start playback).
  const showStartingPointBanner = (() => {
    if (!startingStop) return false;
    if (currentStopIndex > startingStopIndex) return false; // already moved on
    // If we know the user's location, hide the banner once they're inside
    // the starting stop's radius — they've arrived, playback will begin.
    if (userLocation) {
      const d = calculateDistanceMeters(
        userLocation.lat,
        userLocation.lng,
        parseFloat(startingStop.latitude),
        parseFloat(startingStop.longitude),
      );
      const radius = startingStop.radius_meters || 50;
      if (d <= radius) return false;
    }
    return true;
  })();
  const startingStopDistance = startingStop && userLocation
    ? calculateDistanceMeters(
        userLocation.lat,
        userLocation.lng,
        parseFloat(startingStop.latitude),
        parseFloat(startingStop.longitude),
      )
    : null;
  const formatDistance = (m) => {
    if (m == null) return '';
    if (m < 1000) return `${Math.round(m)} m away`;
    return `${(m / 1000).toFixed(m < 10000 ? 1 : 0)} km away`;
  };

  // Show version choice screen for full_access purchases
  if (showVersionChoice && hasExpressOption) {
    const expressStopCount = allStops.filter(s => s.include_in_express).length;
    const fullStopCount = allStops.length;
    
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-900 via-slate-800 to-cyan-900 flex items-center justify-center p-4">
        <div className="bg-white rounded-3xl shadow-2xl max-w-md w-full p-8 text-center">
          <div className="w-20 h-20 bg-cyan-100 rounded-full flex items-center justify-center mx-auto mb-6">
            <MapPin className="w-10 h-10 text-cyan-600" />
          </div>
          <h1 className="text-3xl font-black text-slate-900 mb-2">{purchase?.tour?.title}</h1>
          <h2 className="text-xl font-bold text-cyan-600 mb-6">Choose Your Experience</h2>
          
          <div className="space-y-4 mb-6">
            {/* Full Tour Option */}
            <button
              onClick={() => handleVersionSelect('full')}
              data-testid="select-full-tour"
              className="w-full p-5 rounded-xl border-3 border-cyan-400 bg-cyan-50 hover:bg-cyan-100 transition-all text-left group hover:shadow-lg"
            >
              <div className="flex items-center gap-3 mb-2">
                <span className="text-2xl">📍</span>
                <span className="text-lg font-black text-slate-900 uppercase">Full Tour</span>
              </div>
              <p className="text-slate-600 text-sm mb-2">
                The complete experience with all {fullStopCount} stops
              </p>
              <div className="flex items-center justify-between text-xs text-slate-500">
                <span className="font-semibold">{fullStopCount} stops</span>
                <span className="text-cyan-600 font-bold group-hover:translate-x-1 transition-transform">Start Full Tour →</span>
              </div>
            </button>
            
            {/* Express Tour Option */}
            <button
              onClick={() => handleVersionSelect('express')}
              data-testid="select-express-tour"
              className="w-full p-5 rounded-xl border-3 border-amber-400 bg-amber-50 hover:bg-amber-100 transition-all text-left group hover:shadow-lg"
            >
              <div className="flex items-center gap-3 mb-2">
                <span className="text-2xl">⚡</span>
                <span className="text-lg font-black text-slate-900 uppercase">Express Tour</span>
              </div>
              <p className="text-slate-600 text-sm mb-2">
                Short on time? Hit the highlights with our condensed version
              </p>
              <div className="flex items-center justify-between text-xs text-slate-500">
                <span className="font-semibold">{expressStopCount} highlight stops</span>
                <span className="text-amber-600 font-bold group-hover:translate-x-1 transition-transform">Start Express →</span>
              </div>
            </button>
          </div>
          
          <p className="text-slate-400 text-xs">
            You can always come back and try the other version!
          </p>

          {/* Manual "Start from beginning" — shown only when saved resume
              state exists for this purchase. Lets the user reset the tour
              on demand (per requirement: "Or, it should start over if the
              user manually starts it over"). */}
          {(() => {
            if (!resumeStorageKey) return null;
            try {
              const raw = localStorage.getItem(resumeStorageKey);
              if (!raw) return null;
              const saved = JSON.parse(raw);
              if (!saved || saved.completed) return null;
              return (
                <button
                  type="button"
                  onClick={() => {
                    try { localStorage.removeItem(resumeStorageKey); } catch (_) { /* noop */ }
                    clearBackendProgress();
                    toast.success('Cleared saved progress — the next stop you pick will start fresh');
                  }}
                  data-testid="version-choice-start-over"
                  className="mt-3 text-slate-500 hover:text-slate-800 underline text-xs"
                >
                  Or start this tour from the beginning
                </button>
              );
            } catch (_) {
              return null;
            }
          })()}
          
          {/* Admin Test Mode - select before starting */}
          {isAdmin && (
            <div className="mt-6 pt-6 border-t border-slate-200">
              <p className="text-purple-700 font-bold text-sm mb-3">Admin: Test Mode</p>
              {testMaps.length === 0 ? (
                <p className="text-slate-400 text-xs">No test maps created. <span className="underline cursor-pointer" onClick={() => navigate('/admin/test-settings')}>Create one</span></p>
              ) : (
                <div className="space-y-2">
                  {testMaps.map((tm) => (
                    <button
                      key={tm.map_id}
                      onClick={() => {
                        setSelectedTestMap(tm);
                        setTestMode(true);
                        toast.success(`Test mode: ${tm.name}`);
                      }}
                      data-testid={`select-test-map-${tm.map_id}`}
                      className={`w-full text-left p-3 rounded-lg border-2 transition-all text-sm ${
                        selectedTestMap?.map_id === tm.map_id
                          ? 'bg-purple-100 border-purple-500 text-purple-800'
                          : 'bg-purple-50 border-purple-200 hover:border-purple-400 text-purple-700'
                      }`}
                    >
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-2">
                          <FlaskConical className="w-4 h-4" />
                          <span className="font-bold">{tm.name}</span>
                        </div>
                        <span className="text-xs">{tm.locations?.length || 0} pins</span>
                      </div>
                    </button>
                  ))}
                  {testMode && selectedTestMap && (
                    <p className="text-green-600 text-xs font-semibold text-center mt-1">
                      ✓ Test mode active — select Full or Express above to start
                    </p>
                  )}
                </div>
              )}
            </div>
          )}
          
          <button
            onClick={() => navigate('/my-tours')}
            className="mt-4 text-slate-500 hover:text-slate-700 text-sm"
          >
            Go Back
          </button>
        </div>
      </div>
    );
  }

  // Newsletter subscribe handler for tour completion
  const handleNewsletterSubscribe = async () => {
    if (!newsletterEmail) {
      toast.error('Please enter your email');
      return;
    }
    setNewsletterSubmitting(true);
    try {
      await axios.post(`${API}/newsletter/subscribe`, {
        email: newsletterEmail,
        name: newsletterName || undefined
      });
      setNewsletterDone(true);
      toast.success('You\'re subscribed!');
    } catch (err) {
      toast.error('Subscription failed. Please try again.');
    } finally {
      setNewsletterSubmitting(false);
    }
  };

  // Tour Completed Screen
  if (tourCompleted) {
    const sessionStartMs = parseInt(analyticsSessionId.split('-')[1]) || Date.now();
    const sessionDurationSec = Math.max(0, (Date.now() - sessionStartMs) / 1000);
    const liveWalkedMeters = walkedMetersRef.current || 0;
    const liveWalkedSeconds = walkStartTsRef.current
      ? Math.max(0, Math.round((Date.now() - walkStartTsRef.current) / 1000))
      : Math.round(sessionDurationSec);
    const newsletterBlock = !newsletterDone ? (
      <div className="bg-white/10 backdrop-blur-md rounded-2xl p-6 border border-white/20" data-testid="newsletter-tour-complete">
        <div className="flex items-center gap-3 mb-3">
          <Mail className="w-6 h-6 text-teal-300" />
          <h2 className="text-xl font-bold text-white">Stay in the Loop</h2>
        </div>
        <p className="text-teal-100 text-sm mb-5">
          Be the first to discover new tours, get insider travel tips, and receive exclusive deals before anyone else.
        </p>
        <div className="space-y-3">
          <input
            type="text"
            value={newsletterName}
            onChange={(e) => setNewsletterName(e.target.value)}
            placeholder="Your name"
            data-testid="tour-complete-newsletter-name"
            className="w-full px-4 py-3 rounded-xl bg-white/10 border border-white/20 text-white placeholder-teal-300/60 focus:outline-none focus:ring-2 focus:ring-teal-400/50 focus:border-teal-400"
          />
          <input
            type="email"
            value={newsletterEmail}
            onChange={(e) => setNewsletterEmail(e.target.value)}
            placeholder="Your email"
            data-testid="tour-complete-newsletter-email"
            className="w-full px-4 py-3 rounded-xl bg-white/10 border border-white/20 text-white placeholder-teal-300/60 focus:outline-none focus:ring-2 focus:ring-teal-400/50 focus:border-teal-400"
          />
          <button
            onClick={handleNewsletterSubscribe}
            disabled={newsletterSubmitting}
            data-testid="tour-complete-newsletter-submit"
            className="w-full bg-gradient-to-r from-teal-500 to-cyan-500 hover:from-teal-600 hover:to-cyan-600 text-white py-3 rounded-xl font-bold text-lg shadow-lg shadow-teal-500/30 transition-all active:scale-95 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {newsletterSubmitting ? 'Subscribing...' : 'Count Me In'}
          </button>
        </div>
        <p className="text-teal-300/60 text-xs mt-3 text-center">No spam, ever. Unsubscribe anytime.</p>
      </div>
    ) : (
      <div className="bg-teal-500/20 backdrop-blur-md rounded-2xl p-6 border border-teal-400/30 text-center" data-testid="newsletter-success-message">
        <Check className="w-10 h-10 text-teal-300 mx-auto mb-2" />
        <p className="text-white font-semibold text-lg">You're on the list!</p>
        <p className="text-teal-200 text-sm">We'll keep you posted on new tours and exclusive offers.</p>
      </div>
    );

    return (
      <>
        <TourCompletionExperience
          tour={purchase?.tour}
          user={user}
          sessionDurationSec={sessionDurationSec}
          walkedDistanceMeters={liveWalkedMeters}
          walkedDurationSeconds={liveWalkedSeconds}
          stops={stops}
          stridesEarned={stridesEarnedThisSession}
          newlyUnlockedAchievements={newlyUnlockedAchievements}
          selectedVersion={selectedVersion}
          purchase={purchase}
          tourUrl={purchase?.tour?.tour_id ? `${window.location.origin}/tours/${purchase.tour.tour_id}` : window.location.origin}
          token={token}
          apiBase={API}
          onShareClick={() => setShowShareCard(true)}
          onBackToTours={() => navigate('/my-tours')}
          onExploreMore={() => navigate('/')}
          NewsletterSlot={newsletterBlock}
        />
        {showShareCard && (
          <ShareWalkCard
            tour={purchase?.tour}
            userName={(user?.name || 'A traveler').split(' ')[0]}
            stats={{
              stops: stops.length,
              stridesEarned: stridesEarnedThisSession,
            }}
            achievements={newlyUnlockedAchievements}
            tourUrl={purchase?.tour?.tour_id ? `${window.location.origin}/tours/${purchase.tour.tour_id}?ref=${user?.referral_code || ''}` : window.location.origin}
            onClose={() => setShowShareCard(false)}
            onShared={handleSocialShared}
          />
        )}
      </>
    );
  }

  // Navigation choice is no longer a blocking screen — GPS runs continuously
  // The tour player always stays active so proximity checks keep working

  return (
    <div className="h-screen flex flex-col bg-slate-50" onClick={unlockAudio} onTouchStart={unlockAudio}>
      {/* Walking Stats modal — shows live distance walked + time elapsed.
          Especially useful when a traveller pauses playback and wants to
          know how far they've gone so far. Also shown on completion. */}
      {showStatsModal && (() => {
        const { meters, seconds } = getWalkStats();
        const pace = formatWalkingPace(meters, seconds);
        return (
          <div
            className="fixed inset-0 bg-black/70 z-[100] flex items-end sm:items-center justify-center p-0 sm:p-4"
            onClick={() => setShowStatsModal(false)}
            data-testid="walking-stats-modal"
          >
            <div
              className="bg-gradient-to-br from-slate-900 via-teal-900 to-slate-900 text-white w-full sm:max-w-md sm:rounded-3xl rounded-t-3xl p-6 shadow-2xl border-t sm:border border-white/10"
              onClick={(e) => e.stopPropagation()}
              /* walkStatsTick keeps the render fresh — read but no-op */
              data-tick={walkStatsTick}
            >
              <div className="flex items-start justify-between mb-5">
                <div>
                  <div className="flex items-center gap-2 mb-1">
                    <Activity className="w-5 h-5 text-teal-300" />
                    <p className="text-[0.65rem] uppercase tracking-widest text-teal-300/80 font-bold">
                      Your walk so far
                    </p>
                  </div>
                  <h3 className="text-2xl font-black leading-tight">Keep going!</h3>
                </div>
                <button
                  onClick={() => setShowStatsModal(false)}
                  data-testid="walking-stats-close"
                  className="w-9 h-9 rounded-full bg-white/10 hover:bg-white/20 flex items-center justify-center transition"
                  title="Close"
                >
                  <X className="w-5 h-5" />
                </button>
              </div>

              <div className="grid grid-cols-2 gap-3 mb-4">
                <div
                  className="bg-white/5 border border-white/10 rounded-2xl p-4"
                  data-testid="walking-stats-distance-card"
                >
                  <div className="flex items-center gap-2 mb-1.5">
                    <MapPin className="w-4 h-4 text-teal-300" />
                    <p className="text-[0.65rem] uppercase tracking-widest text-teal-300/70 font-bold">
                      Distance
                    </p>
                  </div>
                  <p className="text-3xl font-black leading-none" data-testid="walking-stats-distance-value">
                    {formatWalkedDistance(meters)}
                  </p>
                </div>
                <div
                  className="bg-white/5 border border-white/10 rounded-2xl p-4"
                  data-testid="walking-stats-time-card"
                >
                  <div className="flex items-center gap-2 mb-1.5">
                    <Clock className="w-4 h-4 text-teal-300" />
                    <p className="text-[0.65rem] uppercase tracking-widest text-teal-300/70 font-bold">
                      Time
                    </p>
                  </div>
                  <p className="text-3xl font-black leading-none font-mono" data-testid="walking-stats-time-value">
                    {formatWalkedDuration(seconds)}
                  </p>
                </div>
              </div>

              {pace ? (
                <div className="bg-white/5 border border-white/10 rounded-2xl p-3 mb-4 flex items-center justify-between">
                  <span className="text-[0.65rem] uppercase tracking-widest text-teal-300/70 font-bold">
                    Avg pace
                  </span>
                  <span className="text-lg font-black" data-testid="walking-stats-pace-value">{pace}</span>
                </div>
              ) : (
                <p className="text-xs text-teal-200/70 leading-relaxed mb-4">
                  Once you&apos;ve walked a bit further we&apos;ll show your average pace here.
                </p>
              )}

              <p className="text-xs text-teal-200/70 leading-relaxed mb-4">
                Distance is tracked from your live GPS. Weak signal (indoors, dense buildings) can undercount — we&apos;ll pick up again once you&apos;re back outside.
              </p>

              <button
                onClick={() => setShowStatsModal(false)}
                data-testid="walking-stats-resume-button"
                className="w-full bg-gradient-to-r from-teal-500 to-cyan-500 hover:brightness-110 text-white py-3 rounded-xl font-bold text-base shadow-lg shadow-teal-500/30 transition-all active:scale-95"
              >
                Back to tour
              </button>
            </div>
          </div>
        );
      })()}

      {/* Low Power Mode warning — Wake Lock request failed on iOS Safari.
          We can't detect LPM directly, but a failed Wake Lock is a strong
          signal. We say "may be in" not "is in" because we can't be sure. */}
      {showLowPowerWarning && (
        <div
          className="fixed inset-0 bg-black/80 z-[100] flex items-center justify-center p-4"
          onClick={(e) => e.stopPropagation()}
          data-testid="low-power-warning-modal"
        >
          <div className="bg-white rounded-2xl max-w-md w-full p-6 shadow-2xl">
            <div className="flex items-start gap-3 mb-3">
              <div className="w-10 h-10 rounded-full bg-amber-100 flex items-center justify-center flex-shrink-0">
                <span className="text-2xl">🔋</span>
              </div>
              <div className="flex-1">
                <h3 className="font-black text-lg text-slate-900 mb-1">
                  Low Power Mode may be on
                </h3>
                <p className="text-sm text-slate-600 leading-relaxed">
                  We tried to keep your screen awake for the tour, but iOS blocked us — usually because Low Power Mode is on.
                </p>
              </div>
            </div>
            <div className="bg-amber-50 border border-amber-200 rounded-xl p-4 mb-4">
              <p className="text-sm font-bold text-amber-900 mb-2">To turn it off:</p>
              <ol className="text-sm text-amber-900 space-y-1 list-decimal list-inside">
                <li>Open <strong>Settings</strong></li>
                <li>Tap <strong>Battery</strong></li>
                <li>Turn off <strong>Low Power Mode</strong></li>
                <li>Come back to Audiostride and tap below</li>
              </ol>
            </div>
            <p className="text-xs text-slate-500 mb-4">
              If you stay in Low Power Mode, your screen may dim and audio for the next stop might not start automatically. You&apos;ll need to tap the screen at each new location.
            </p>
            <div className="flex flex-col gap-2">
              <button
                type="button"
                onClick={() => {
                  setShowLowPowerWarning(false);
                  // Retry Wake Lock — user may have just turned LPM off
                  setTimeout(() => requestWakeLock(), 250);
                }}
                data-testid="low-power-retry"
                className="w-full bg-slate-900 hover:bg-slate-800 text-white font-bold rounded-xl py-3 active:scale-[0.99] transition"
              >
                I turned it off — try again
              </button>
              <button
                type="button"
                onClick={() => setShowLowPowerWarning(false)}
                data-testid="low-power-dismiss"
                className="w-full bg-white border border-slate-300 hover:bg-slate-50 text-slate-700 font-bold rounded-xl py-3 active:scale-[0.99] transition"
              >
                Continue anyway
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Resume Banner — surfaces when iOS suspends/interrupts the audio session
          (incoming call, long screen-lock, switching to another media app, etc.) */}
      {needsResume && (
        <button
          type="button"
          onClick={(e) => { e.stopPropagation(); tryResume(); }}
          data-testid="resume-tour-banner"
          className="w-full bg-gradient-to-r from-amber-400 via-orange-500 to-rose-500 text-white py-3 px-4 flex items-center justify-center gap-3 shadow-lg active:scale-[0.99] transition-transform z-20"
        >
          <Play className="w-5 h-5" />
          <span className="font-bold text-base sm:text-lg">
            Tour paused — tap to resume
          </span>
        </button>
      )}
      <header className="bg-slate-900 text-white p-4 flex items-center justify-between z-10">
        <button
          onClick={() => navigate('/my-tours')}
          data-testid="back-to-my-tours-button"
          className="flex items-center text-teal-400 hover:text-teal-300"
        >
          <ArrowLeft className="w-5 h-5 mr-2" />
          Exit Tour
        </button>
        <div className="text-center flex-1 px-2">
          <div className="flex items-center justify-center gap-2">
            <h1 className="text-sm sm:text-base font-bold font-sans truncate">{purchase?.tour?.title}</h1>
            {selectedVersion === 'express' && (
              <span className="bg-amber-500 text-white text-xs font-bold px-2 py-0.5 rounded-full">
                ⚡
              </span>
            )}
          </div>
          <p className="text-xs text-slate-400 font-body">
            Stop {currentStopIndex + 1} of {stops.length} · {Math.round(((currentStopIndex + 1) / Math.max(stops.length, 1)) * 100)}%
          </p>
        </div>
        {/* Walking Stats trigger — always available so travellers can peek
            at how far / how long they've walked, especially when paused. */}
        <button
          onClick={() => setShowStatsModal(true)}
          data-testid="walking-stats-button"
          title="Walking stats"
          className="w-[88px] flex items-center justify-end gap-1.5 text-teal-300 hover:text-teal-200 active:scale-95 transition"
        >
          <Activity className="w-5 h-5" />
          <span className="text-xs font-bold uppercase tracking-wider">Stats</span>
        </button>
      </header>

      {/* Starting-Point Banner — very clear "walk here to begin" cue.
          Shows until the user is inside the first GPS-marked stop's radius
          (or has advanced past it). Anchored below the header so it's the
          first thing users see when they open the tour. */}
      {showStartingPointBanner && (
        <div
          data-testid="starting-point-banner"
          className="bg-gradient-to-r from-teal-500 to-cyan-500 text-white px-4 py-3 shadow-md"
        >
          <div className="max-w-3xl mx-auto flex items-center gap-3">
            <div className="shrink-0 w-10 h-10 rounded-full bg-white/20 flex items-center justify-center">
              <MapPin className="w-5 h-5" />
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-[11px] uppercase tracking-wider font-bold opacity-90">
                Starting point
              </p>
              <p className="text-sm sm:text-base font-bold truncate" data-testid="starting-point-title">
                Head to: {startingStop.title}
              </p>
              {startingStopDistance != null && (
                <p className="text-xs opacity-90" data-testid="starting-point-distance">
                  {formatDistance(startingStopDistance)}
                </p>
              )}
            </div>
            <button
              type="button"
              onClick={() => openGoogleMaps(startingStop.latitude, startingStop.longitude, startingStop.title)}
              data-testid="starting-point-directions-button"
              className="shrink-0 bg-white text-teal-700 hover:bg-teal-50 px-3 py-2 rounded-full text-xs sm:text-sm font-bold flex items-center gap-1.5 shadow-sm"
            >
              <Navigation className="w-4 h-4" />
              Directions
            </button>
          </div>
        </div>
      )}

      {/* Test Mode Banner (Admin Only) */}
      {isAdmin && testMode && (
        <div className="bg-purple-600 text-white px-4 py-2 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <FlaskConical className="w-5 h-5" />
            <span className="font-bold">TEST MODE</span>
            <span className="text-purple-200">Using: {selectedTestMap?.name}</span>
          </div>
          <button
            onClick={handleTestModeToggle}
            className="text-sm bg-purple-700 hover:bg-purple-800 px-3 py-1 rounded-full"
          >
            Disable
          </button>
        </div>
      )}
      
      {/* Test Mode Toggle Button (Admin Only) */}
      {isAdmin && !testMode && (
        <button
          onClick={handleTestModeToggle}
          className="absolute top-20 right-4 z-20 bg-purple-600 hover:bg-purple-700 text-white px-3 py-2 rounded-full flex items-center gap-2 shadow-lg text-sm"
          data-testid="test-mode-toggle"
        >
          <FlaskConical className="w-4 h-4" />
          Test Mode
        </button>
      )}
      
      {/* Test Mode Panel (Admin Only) */}
      {showTestModePanel && (
        <div className="fixed inset-0 bg-black/80 z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-2xl max-w-md w-full p-6">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-xl font-bold text-gray-900 flex items-center gap-2">
                <FlaskConical className="w-6 h-6 text-purple-600" />
                Enable Test Mode
              </h3>
              <button
                onClick={() => setShowTestModePanel(false)}
                className="p-2 hover:bg-gray-100 rounded-full"
              >
                ✕
              </button>
            </div>
            <p className="text-gray-600 text-sm mb-4">
              Select a test map to use your test locations instead of the actual tour locations.
            </p>
            
            {testMaps.length === 0 ? (
              <div className="text-center py-6">
                <p className="text-gray-500 mb-4">No test maps created yet.</p>
                <button
                  onClick={() => navigate('/admin/test-settings')}
                  className="bg-purple-600 hover:bg-purple-700 text-white px-4 py-2 rounded-full font-bold"
                >
                  Create Test Map
                </button>
              </div>
            ) : (
              <div className="space-y-2 max-h-64 overflow-y-auto">
                {testMaps.map((testMap) => (
                  <button
                    key={testMap.map_id}
                    onClick={() => applyTestMap(testMap)}
                    className="w-full text-left p-4 bg-gray-50 hover:bg-purple-50 rounded-lg border-2 border-transparent hover:border-purple-300 transition-all"
                  >
                    <div className="font-bold text-gray-900">{testMap.name}</div>
                    <div className="text-sm text-gray-500">
                      {testMap.locations?.length || 0} test location{testMap.locations?.length !== 1 ? 's' : ''}
                    </div>
                  </button>
                ))}
              </div>
            )}
          </div>
        </div>
      )}

      {/* Full Tour Map Modal */}
      {showFullTourMap && (
        <div className="fixed inset-0 bg-black/80 z-50 flex flex-col">
          {/* Pulse animation style */}
          <style>{`
            @keyframes pulse {
              0% { box-shadow: 0 0 0 4px #F97316, 0 4px 12px rgba(0,0,0,0.4); }
              50% { box-shadow: 0 0 0 8px rgba(249,115,22,0.4), 0 4px 12px rgba(0,0,0,0.4); }
              100% { box-shadow: 0 0 0 4px #F97316, 0 4px 12px rgba(0,0,0,0.4); }
            }
            @keyframes pulse-start {
              0% { box-shadow: 0 0 0 4px #0EA5E9, 0 4px 14px rgba(0,0,0,0.4); }
              50% { box-shadow: 0 0 0 10px rgba(14,165,233,0.35), 0 4px 14px rgba(0,0,0,0.4); }
              100% { box-shadow: 0 0 0 4px #0EA5E9, 0 4px 14px rgba(0,0,0,0.4); }
            }
          `}</style>
          
          <div className="bg-slate-900 text-white p-4 flex items-center justify-between">
            <div>
              <h2 className="text-lg font-bold">{purchase?.tour?.title}</h2>
              <p className="text-sm text-slate-400">
                {selectedVersion === 'express' ? `⚡ Express Tour - ${stops.length} Stops` : `Full Tour - ${stops.length} Stops`}
              </p>
            </div>
            <button
              onClick={handleStartTourFromMap}
              data-testid="all-stops-start-tour"
              className="bg-green-500 hover:bg-green-600 text-white px-5 py-2 rounded-full font-bold flex items-center gap-2"
            >
              <Play className="w-5 h-5" />
              Start Tour
            </button>
          </div>
          
          {/* Legend */}
          <div className="bg-white/95 px-4 py-2 flex items-center justify-center gap-6 text-sm border-b">
            <div className="flex items-center gap-2">
              <div className="w-6 h-6 rounded-full bg-orange-500 flex items-center justify-center text-white text-xs font-bold border-2 border-white shadow">•</div>
              <span className="text-gray-700">Current Stop</span>
            </div>
            <div className="flex items-center gap-2">
              <div className={`w-5 h-5 rounded-full ${selectedVersion === 'express' ? 'bg-amber-500' : 'bg-teal-500'} flex items-center justify-center text-white text-xs font-bold border-2 border-white shadow`}>•</div>
              <span className="text-gray-700">Other Stops</span>
            </div>
            <div className="flex items-center gap-2">
              <div className="w-4 h-4 rounded-full bg-blue-500 border-2 border-white shadow"></div>
              <span className="text-gray-700">You</span>
            </div>
          </div>
          
          {/* Tap instruction */}
          <div className="bg-amber-50 px-4 py-2 text-center text-sm text-amber-800 font-semibold">
            👆 Tap any stop on the map to navigate to it
          </div>
          
          <div className="flex-1 relative">
            {stops.length > 0 && stops.some(s => s.latitude && s.longitude) && (
              <MapContainer
                center={[
                  stops.filter(s => s.latitude && s.longitude).reduce((sum, s) => sum + parseFloat(s.latitude), 0) / stops.filter(s => s.latitude && s.longitude).length,
                  stops.filter(s => s.latitude && s.longitude).reduce((sum, s) => sum + parseFloat(s.longitude), 0) / stops.filter(s => s.latitude && s.longitude).length
                ]}
                zoom={14}
                style={{ height: '100%', width: '100%' }}
                zoomControl={true}
              >
                <TileLayer
                  url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
                  attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>'
                />
                
                {/* User location */}
                {userLocation && (
                  <Marker position={[userLocation.lat, userLocation.lng]}>
                    <Circle
                      center={[userLocation.lat, userLocation.lng]}
                      radius={15}
                      pathOptions={{ color: '#3B82F6', fillColor: '#3B82F6', fillOpacity: 0.5 }}
                    />
                  </Marker>
                )}
                
                {/* Walking line: user → current stop (animated dashed teal) */}
                {userLocation && currentStop && currentStop.latitude && currentStop.longitude && !currentStop.is_intro && (
                  <Polyline
                    positions={[
                      [userLocation.lat, userLocation.lng],
                      [parseFloat(currentStop.latitude), parseFloat(currentStop.longitude)],
                    ]}
                    pathOptions={{
                      color: '#14B8A6',
                      weight: 5,
                      opacity: 0.9,
                      dashArray: '10, 8',
                      lineCap: 'round',
                    }}
                  />
                )}
                
                {/* Route line: connects all stops in order */}
                {stops.filter(s => s.latitude && s.longitude).length > 1 && (
                  <Polyline
                    positions={stops
                      .filter(s => s.latitude && s.longitude)
                      .map(s => [parseFloat(s.latitude), parseFloat(s.longitude)])}
                    pathOptions={{
                      color: '#94A3B8',
                      weight: 3,
                      opacity: 0.5,
                      dashArray: '6, 6',
                      lineCap: 'round',
                    }}
                  />
                )}
                
                {/* All stops with numbers */}
                {stops.filter(stop => stop.latitude && stop.longitude).map((stop, index) => {
                  const isStartingStop = index === startingStopIndex && showStartingPointBanner;
                  return (
                  <React.Fragment key={stop.stop_id}>
                    <Marker
                      position={[parseFloat(stop.latitude), parseFloat(stop.longitude)]}
                      icon={L.divIcon({
                        className: 'custom-numbered-marker',
                        html: index === currentStopIndex 
                          ? `<div style="
                              background: #F97316;
                              color: white;
                              width: 44px;
                              height: 44px;
                              border-radius: 50%;
                              display: flex;
                              align-items: center;
                              justify-content: center;
                              font-weight: bold;
                              font-size: 16px;
                              border: 4px solid white;
                              box-shadow: 0 0 0 4px #F97316, 0 4px 12px rgba(0,0,0,0.4);
                              animation: pulse 2s infinite;
                            ">
                              <span style="display:flex;flex-direction:column;align-items:center;line-height:1;">
                                <span style="font-size:10px;">YOU</span>
                                <span>${index + 1}</span>
                              </span>
                            </div>`
                          : isStartingStop
                          ? `<div style="
                              position: relative;
                              background: #0EA5E9;
                              color: white;
                              width: 52px;
                              height: 52px;
                              border-radius: 50%;
                              display: flex;
                              align-items: center;
                              justify-content: center;
                              font-weight: 800;
                              font-size: 10px;
                              border: 4px solid white;
                              box-shadow: 0 0 0 4px #0EA5E9, 0 4px 14px rgba(0,0,0,0.4);
                              animation: pulse-start 2.4s infinite;
                            ">
                              <span style="display:flex;flex-direction:column;align-items:center;line-height:1.05;letter-spacing:0.5px;">
                                <span style="font-size:16px;">📍</span>
                                <span>START</span>
                              </span>
                            </div>`
                          : `<div style="
                              background: ${selectedVersion === 'express' ? '#F59E0B' : '#14B8A6'};
                              color: white;
                              width: 32px;
                              height: 32px;
                              border-radius: 50%;
                              display: flex;
                              align-items: center;
                              justify-content: center;
                              font-weight: bold;
                              font-size: 14px;
                              border: 3px solid white;
                              box-shadow: 0 2px 6px rgba(0,0,0,0.3);
                              cursor: pointer;
                            ">${index + 1}</div>`,
                        iconSize: index === currentStopIndex ? [44, 44] : (isStartingStop ? [52, 52] : [32, 32]),
                        iconAnchor: index === currentStopIndex ? [22, 22] : (isStartingStop ? [26, 26] : [16, 16]),
                      })}
                      eventHandlers={{
                        click: () => {
                          setCurrentStopIndex(index);
                          setShowFullTourMap(false);
                          toast.success(`Navigating to Stop ${index + 1}: ${stop.title}`);
                        }
                      }}
                    />
                    <Circle
                      center={[parseFloat(stop.latitude), parseFloat(stop.longitude)]}
                      radius={stop.radius_meters || 50}
                      pathOptions={{
                        color: index === currentStopIndex ? '#F97316' : (isStartingStop ? '#0EA5E9' : (selectedVersion === 'express' ? '#F59E0B' : '#14B8A6')),
                        fillColor: index === currentStopIndex ? '#F97316' : (isStartingStop ? '#0EA5E9' : (selectedVersion === 'express' ? '#F59E0B' : '#14B8A6')),
                        fillOpacity: index === currentStopIndex ? 0.3 : (isStartingStop ? 0.25 : 0.15),
                        weight: index === currentStopIndex ? 3 : (isStartingStop ? 3 : 2),
                      }}
                      eventHandlers={{
                        click: () => {
                          setCurrentStopIndex(index);
                          setShowFullTourMap(false);
                          toast.success(`Navigating to Stop ${index + 1}: ${stop.title}`);
                        }
                      }}
                    />
                  </React.Fragment>
                  );
                })}
              </MapContainer>
            )}
          </div>
          
          {/* Stops List */}
          <div className="bg-white max-h-56 overflow-y-auto border-t">
            <div className="p-2">
              <p className="text-xs text-gray-500 font-semibold mb-2 px-2 uppercase tracking-wide">
                {selectedVersion === 'express' ? '⚡ Express Stops · Tap to jump' : `${stops.length} stops · Tap to jump`}
              </p>
              {stops.map((stop, index) => {
                const isCurrent = index === currentStopIndex;
                const isDone = index < currentStopIndex;
                const distToStop = userLocation && stop.latitude && stop.longitude && !stop.is_intro
                  ? calculateDistanceMeters(userLocation.lat, userLocation.lng, parseFloat(stop.latitude), parseFloat(stop.longitude))
                  : null;
                return (
                  <div
                    key={stop.stop_id}
                    onClick={() => {
                      setCurrentStopIndex(index);
                      setShowFullTourMap(false);
                    }}
                    data-testid={`all-stops-list-${index}`}
                    className={`flex items-center gap-3 p-2.5 rounded-lg cursor-pointer transition-all active:scale-[0.99] ${
                      isCurrent
                        ? 'bg-gradient-to-r from-orange-50 to-amber-50 border-l-4 border-orange-500'
                        : 'hover:bg-gray-100'
                    }`}
                  >
                    <div className={`w-9 h-9 rounded-xl flex items-center justify-center text-white font-black text-sm flex-shrink-0 shadow-sm ${
                      isCurrent
                        ? 'bg-orange-500'
                        : isDone
                          ? 'bg-slate-300'
                          : (selectedVersion === 'express' ? 'bg-amber-500' : 'bg-teal-500')
                    }`}>
                      {isDone ? <Check className="w-4 h-4" /> : (index + 1)}
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className={`font-semibold text-sm truncate ${
                        isCurrent ? 'text-slate-900' : isDone ? 'text-slate-400 line-through' : 'text-gray-800'
                      }`}>
                        {stop.title}
                      </p>
                      <p className={`text-[11px] truncate ${
                        isCurrent ? 'text-orange-700 font-bold' : 'text-gray-500'
                      }`}>
                        {isDone
                          ? 'Completed'
                          : distToStop != null
                            ? `📍 ${distToStop < 1000 ? `${Math.round(distToStop)}m` : `${(distToStop / 1000).toFixed(1)}km`} away`
                            : stop.is_intro ? 'Introduction' : 'Coming up'}
                      </p>
                    </div>
                    {isCurrent && (
                      <span className="text-[10px] bg-orange-500 text-white font-black px-2 py-1 rounded-full uppercase">Now</span>
                    )}
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      )}

      <div className="flex-1 relative">
        {mapCenter && (
          <MapContainer
            center={[mapCenter.lat, mapCenter.lng]}
            zoom={16}
            style={{ height: '100%', width: '100%' }}
            zoomControl={false}
          >
            <TileLayer
              url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
              attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>'
            />
            <MapUpdater center={userLocation ? [userLocation.lat, userLocation.lng] : null} />
            
            {/* Walking line: from user to current stop (animated dashed teal) */}
            {userLocation && currentStop && currentStop.latitude && currentStop.longitude && !currentStop.is_intro && (
              <Polyline
                positions={[
                  [userLocation.lat, userLocation.lng],
                  [parseFloat(currentStop.latitude), parseFloat(currentStop.longitude)],
                ]}
                pathOptions={{
                  color: '#14B8A6',
                  weight: 5,
                  opacity: 0.85,
                  dashArray: '10, 8',
                  lineCap: 'round',
                }}
              />
            )}
            
            {/* Future path: connect upcoming stops with a faint dashed line */}
            {stops.length > currentStopIndex + 1 && (
              <Polyline
                positions={stops
                  .slice(currentStopIndex, stops.length)
                  .filter(s => s.latitude && s.longitude)
                  .map(s => [parseFloat(s.latitude), parseFloat(s.longitude)])}
                pathOptions={{
                  color: '#94A3B8',
                  weight: 3,
                  opacity: 0.6,
                  dashArray: '6, 6',
                  lineCap: 'round',
                }}
              />
            )}
            
            {userLocation && (
              <Marker position={[userLocation.lat, userLocation.lng]}>
                <Circle
                  center={[userLocation.lat, userLocation.lng]}
                  radius={10}
                  pathOptions={{ color: '#14B8A6', fillColor: '#14B8A6', fillOpacity: 0.5 }}
                />
              </Marker>
            )}
            
            {stops.filter(stop => stop.latitude && stop.longitude).map((stop, index) => {
              const isStartingStop = index === startingStopIndex && showStartingPointBanner;
              const stopColor = isStartingStop && index !== currentStopIndex ? '#0EA5E9' : (index === currentStopIndex ? '#F97316' : '#14B8A6');
              return (
              <React.Fragment key={stop.stop_id}>
                <Marker
                  position={[parseFloat(stop.latitude), parseFloat(stop.longitude)]}
                  icon={createCustomIcon(index === currentStopIndex, isStartingStop && index !== currentStopIndex)}
                />
                <Circle
                  center={[parseFloat(stop.latitude), parseFloat(stop.longitude)]}
                  radius={stop.radius_meters || 50}
                  pathOptions={{
                    color: stopColor,
                    fillColor: stopColor,
                    fillOpacity: isStartingStop && index !== currentStopIndex ? 0.2 : 0.1,
                  }}
                />
              </React.Fragment>
              );
            })}
          </MapContainer>
        )}

        {!userLocation && (
          <div className="absolute inset-0 flex items-center justify-center bg-slate-900/50 z-10">
            <div className="bg-white p-6 rounded-2xl shadow-xl text-center max-w-xs">
              <Navigation className="w-12 h-12 text-teal-500 mx-auto mb-4 animate-spin" />
              <p className="text-slate-900 font-semibold font-body mb-1">Finding your location…</p>
              <p className="text-slate-500 text-sm mb-4">This usually takes a few seconds outdoors. Inside a building? Step closer to a window.</p>
              <button
                type="button"
                onClick={() => { try { startGPSTracking(); } catch (_) { window.location.reload(); } }}
                data-testid="gps-retry-button"
                className="text-teal-600 hover:text-teal-700 text-sm font-bold underline"
              >
                Tap to retry
              </button>
            </div>
          </div>
        )}
      </div>

      {currentStop && (
        <div className="bg-white/95 backdrop-blur-lg border-t border-slate-200 p-4 z-10">
          <div className="max-w-4xl mx-auto">
            {/* PROMINENT ACTION BAR — All Stops + Navigate */}
            <div className="grid grid-cols-2 gap-2.5 mb-3">
              {/* All Stops card */}
              <button
                type="button"
                onClick={() => setShowFullTourMap(true)}
                data-testid="view-all-stops-button"
                className="relative bg-gradient-to-br from-slate-900 to-slate-800 hover:from-slate-800 hover:to-slate-700 text-white rounded-2xl p-3 flex items-center gap-3 shadow-lg active:scale-[0.98] transition-all text-left"
              >
                <span className="absolute top-1.5 right-2 bg-teal-400 text-slate-900 text-[10px] font-black px-1.5 py-0.5 rounded-full">
                  {currentStopIndex + 1}/{stops.length}
                </span>
                <div className="w-9 h-9 rounded-xl bg-teal-500/20 border border-teal-400/40 flex items-center justify-center flex-shrink-0">
                  <MapPin className="w-5 h-5 text-teal-300" />
                </div>
                <div className="min-w-0">
                  <p className="font-black text-sm leading-tight">All Stops</p>
                  <p className="text-[11px] text-slate-400">Map · skip · jump</p>
                </div>
              </button>

              {/* Navigate card */}
              {currentStop.latitude && currentStop.longitude && !currentStop.is_intro ? (
                <a
                  href={preferredNavApp === 'waze'
                    ? `https://waze.com/ul?ll=${currentStop.latitude},${currentStop.longitude}&navigate=yes`
                    : `https://www.google.com/maps/dir/?api=1&destination=${currentStop.latitude},${currentStop.longitude}&travelmode=${getNavigationMode()}`
                  }
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid="navigate-button"
                  className="relative bg-gradient-to-br from-teal-500 to-cyan-600 hover:from-teal-600 hover:to-cyan-700 text-white rounded-2xl p-3 flex items-center gap-3 shadow-lg active:scale-[0.98] transition-all"
                >
                  {getDistanceToStop(currentStop) && (
                    <span className="absolute top-1.5 right-2 bg-white text-teal-700 text-[10px] font-black px-1.5 py-0.5 rounded-full">
                      {getDistanceToStop(currentStop) < 1000
                        ? `${Math.round(getDistanceToStop(currentStop))}m`
                        : `${(getDistanceToStop(currentStop) / 1000).toFixed(1)}km`}
                    </span>
                  )}
                  <div className="w-9 h-9 rounded-xl bg-white/20 border border-white/30 flex items-center justify-center flex-shrink-0">
                    {getNavigationMode() === 'driving' ? (
                      <Car className="w-5 h-5 text-white" />
                    ) : (
                      <Navigation className="w-5 h-5 text-white" />
                    )}
                  </div>
                  <div className="min-w-0 text-left">
                    <p className="font-black text-sm leading-tight">Navigate</p>
                    <p className="text-[11px] text-teal-50">Open in {preferredNavApp === 'waze' ? 'Waze' : 'Maps'}</p>
                  </div>
                </a>
              ) : (
                <div
                  data-testid="navigate-button"
                  aria-disabled="true"
                  className="bg-slate-100 text-slate-400 rounded-2xl p-3 flex items-center gap-3"
                >
                  <div className="w-9 h-9 rounded-xl bg-slate-200 flex items-center justify-center flex-shrink-0">
                    <Navigation className="w-5 h-5" />
                  </div>
                  <div className="min-w-0">
                    <p className="font-black text-sm leading-tight">Navigate</p>
                    <p className="text-[11px]">Intro · no destination</p>
                  </div>
                </div>
              )}
            </div>

            <div className="flex items-center justify-between mb-3">
              <div className="flex items-center flex-1 min-w-0">
                <MapPin className="w-5 h-5 text-teal-500 mr-2 flex-shrink-0" />
                <h3 className="text-lg font-bold text-slate-900 font-sans truncate">{currentStop.title}</h3>
              </div>
              <div className="flex items-center gap-2">
                {/* Skip Button */}
                <button
                  onClick={skipCurrentStop}
                  data-testid="skip-stop-button"
                  className="bg-orange-500 hover:bg-orange-600 text-white p-3 rounded-full shadow-lg hover:shadow-xl transition-all active:scale-95"
                  title="Skip to next stop"
                >
                  <SkipForward className="w-5 h-5" />
                </button>
                {/* Play/Pause Button - always enabled */}
                <button
                  onClick={togglePlayPause}
                  data-testid="audio-toggle-button"
                  className="bg-teal-500 hover:bg-teal-600 text-white p-3 rounded-full shadow-lg hover:shadow-xl transition-all active:scale-95"
                  title={audio ? (playing ? "Pause" : "Resume") : "Play this stop"}
                >
                  {playing ? <Pause className="w-6 h-6" /> : <Play className="w-6 h-6 ml-1" />}
                </button>
              </div>
            </div>
            
            {/* Audio Timer Display */}
            {audio && audioDuration > 0 && (
              <div className="mb-3 bg-slate-100 rounded-xl p-4">
                {/* Audio Control Buttons */}
                <div className="flex items-center justify-center gap-4 mb-3">
                  <button
                    onClick={rewind10}
                    data-testid="rewind-button"
                    className="bg-slate-200 hover:bg-slate-300 text-slate-700 p-2 rounded-full transition-all active:scale-95"
                    title="Rewind 10 seconds"
                  >
                    <Rewind className="w-5 h-5" />
                  </button>
                  <button
                    onClick={togglePlayPause}
                    data-testid="play-pause-main"
                    className="bg-teal-500 hover:bg-teal-600 text-white p-4 rounded-full shadow-lg transition-all active:scale-95"
                  >
                    {playing ? <Pause className="w-8 h-8" /> : <Play className="w-8 h-8 ml-1" />}
                  </button>
                  <button
                    onClick={forward10}
                    data-testid="forward-button"
                    className="bg-slate-200 hover:bg-slate-300 text-slate-700 p-2 rounded-full transition-all active:scale-95"
                    title="Forward 10 seconds"
                  >
                    <FastForward className="w-5 h-5" />
                  </button>
                </div>
                
                {/* Audio Progress Bar */}
                <div className="relative h-3 bg-slate-300 rounded-full overflow-hidden mb-2">
                  <div
                    className="absolute left-0 top-0 h-full bg-teal-500 transition-all duration-200"
                    style={{ width: `${(audioCurrentTime / audioDuration) * 100}%` }}
                  />
                  {/* Clickable seek area */}
                  <input
                    type="range"
                    min="0"
                    max={audioDuration || 100}
                    value={audioCurrentTime}
                    onChange={(e) => {
                      const newTime = parseFloat(e.target.value);
                      if (audio) {
                        audio.currentTime = newTime;
                        setAudioCurrentTime(newTime);
                      }
                    }}
                    className="absolute inset-0 w-full h-full opacity-0 cursor-pointer"
                    data-testid="audio-seek-bar"
                  />
                </div>
                
                {/* Time Display */}
                <div className="flex items-center justify-between text-sm font-mono">
                  <span className="text-slate-600">{formatTime(audioCurrentTime)}</span>
                  <div className="flex items-center gap-2">
                    <span className="text-teal-600 font-bold bg-teal-100 px-2 py-0.5 rounded">
                      -{formatTime(getTimeRemaining())} remaining
                    </span>
                  </div>
                  <span className="text-slate-600">{formatTime(audioDuration)}</span>
                </div>
                
                {/* Drag to seek hint */}
                <p className="text-center text-xs text-slate-500 mt-2">
                  Drag the bar to skip forward or back
                </p>
              </div>
            )}

            {/* Replay directions — visible on every stop EXCEPT the final one.
                Tap to jump to the moment in this stop's audio where the
                narrator starts giving directions to the next stop. Falls
                back to "last 20 seconds" if no explicit timestamp is set. */}
            {currentStopIndex < stops.length - 1 && currentStop?.audio_url && (
              <button
                type="button"
                onClick={replayDirections}
                data-testid="replay-directions-button"
                className={`w-full mb-3 inline-flex items-center justify-center gap-2 bg-[#e1771a] hover:bg-[#c8651a] text-white font-bold rounded-xl px-4 py-3 shadow-md active:scale-[0.99] transition-all ${
                  directionsPulse ? 'animate-pulse ring-4 ring-[#e1771a]/40' : ''
                }`}
                title="Replay the directions to the next stop"
              >
                <Compass className="w-5 h-5" />
                Replay directions to next stop
              </button>
            )}
            
            {/* Show audio controls when not playing */}
            {currentStop.audio_url && !audio && (
              <div className="mb-3 bg-gradient-to-r from-teal-50 to-cyan-50 border border-teal-200 rounded-xl p-4">
                {/* Live distance indicator */}
                {!currentStop.is_intro && userLocation && currentStop.latitude && currentStop.longitude && (
                  <div className="mb-3 text-center">
                    <p className="text-xs text-slate-500 uppercase tracking-wide mb-1">Distance to stop</p>
                    <p className={`text-2xl font-black ${
                      getDistanceToStop(currentStop) && getDistanceToStop(currentStop) <= Math.max(currentStop.radius_meters || 50, 30)
                        ? 'text-green-600'
                        : 'text-slate-800'
                    }`}>
                      {getDistanceToStop(currentStop) 
                        ? getDistanceToStop(currentStop) < 1000 
                          ? `${Math.round(getDistanceToStop(currentStop))}m`
                          : `${(getDistanceToStop(currentStop) / 1000).toFixed(1)}km`
                        : '...'}
                    </p>
                    <p className="text-xs text-slate-500">
                      Triggers within {Math.max(currentStop.radius_meters || 50, 30)}m
                    </p>
                  </div>
                )}
                <p className="text-center text-slate-600 text-sm mb-3">
                  {currentStop.is_intro 
                    ? '🎙️ Ready to play the introduction' 
                    : `📍 Audio will auto-play when you arrive at this stop`}
                </p>
                <button
                  onClick={playCurrentStopManually}
                  data-testid="play-now-button"
                  className="w-full bg-teal-500 hover:bg-teal-600 text-white py-3 rounded-xl font-bold text-lg flex items-center justify-center gap-2 shadow-md hover:shadow-lg transition-all active:scale-95"
                >
                  <Play className="w-5 h-5" />
                  Play Now
                </button>
                <p className="text-center text-xs text-slate-500 mt-2">
                  Or wait for GPS to trigger automatically
                </p>
              </div>
            )}
            
            {/* Navigate is now in the prominent action bar above — no duplicate link here */}
            
            {/* Progress Bar */}
            <div className="h-2 bg-slate-200 rounded-full overflow-hidden mb-3">
              <div
                className="h-full bg-teal-500 transition-all duration-300"
                style={{ width: `${((currentStopIndex + 1) / stops.length) * 100}%` }}
              ></div>
            </div>
            
            {/* Expandable Stop List — quick browse / skip / jump */}
            <button
              onClick={() => setShowStopsList(!showStopsList)}
              data-testid="toggle-stops-list"
              className="w-full flex items-center justify-center gap-2 text-slate-600 hover:text-slate-900 py-2 transition-colors"
            >
              <span className="text-sm font-medium">Browse all {stops.length} stops ({currentStopIndex + 1}/{stops.length})</span>
              {showStopsList ? <ChevronDown className="w-4 h-4" /> : <ChevronUp className="w-4 h-4" />}
            </button>
            
            {showStopsList && (
              <div className="mt-2 max-h-56 overflow-y-auto border border-slate-200 rounded-xl bg-white divide-y divide-slate-100">
                {stops.map((stop, index) => {
                  const isCurrent = index === currentStopIndex;
                  const isDone = index < currentStopIndex;
                  const distToStop = userLocation && stop.latitude && stop.longitude && !stop.is_intro
                    ? calculateDistanceMeters(userLocation.lat, userLocation.lng, parseFloat(stop.latitude), parseFloat(stop.longitude))
                    : null;
                  return (
                    <button
                      key={stop.stop_id}
                      onClick={() => playSpecificStop(index)}
                      data-testid={`stops-list-item-${index}`}
                      className={`w-full flex items-center gap-3 p-3 text-left transition-colors active:scale-[0.99] ${
                        isCurrent
                          ? 'bg-gradient-to-r from-orange-50 to-amber-50 border-l-4 border-orange-500'
                          : 'hover:bg-slate-50'
                      }`}
                    >
                      <span className={`w-9 h-9 rounded-xl flex items-center justify-center text-sm font-black flex-shrink-0 shadow-sm ${
                        isCurrent
                          ? 'bg-orange-500 text-white'
                          : isDone
                            ? 'bg-slate-200 text-slate-500'
                            : 'bg-teal-500 text-white'
                      }`}>
                        {isDone ? <Check className="w-4 h-4" /> : (index + 1)}
                      </span>
                      <div className="flex-1 min-w-0">
                        <p className={`font-semibold text-sm truncate ${
                          isCurrent ? 'text-slate-900' : isDone ? 'text-slate-400 line-through' : 'text-slate-800'
                        }`}>
                          {stop.title}
                        </p>
                        <p className={`text-[11px] truncate ${
                          isCurrent ? 'text-orange-700 font-bold' : 'text-slate-500'
                        }`}>
                          {isDone
                            ? 'Completed'
                            : distToStop != null
                              ? `${distToStop < 1000 ? `${Math.round(distToStop)}m` : `${(distToStop / 1000).toFixed(1)}km`} away`
                              : stop.is_intro ? 'Introduction' : 'Coming up'}
                        </p>
                      </div>
                      {isCurrent && (
                        <span className="text-[10px] bg-orange-500 text-white font-black px-2 py-1 rounded-full uppercase tracking-wide">Now</span>
                      )}
                      {!isCurrent && !isDone && (
                        <span className="text-[10px] bg-slate-900 text-white font-bold px-2 py-1 rounded-lg">Jump</span>
                      )}
                    </button>
                  );
                })}
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default TourPlayerPage;
